<template>
  <div class="blog-article blog-article-container" :style="backgroundStyle">
    <!-- 背景图层 -->
    <div class="background-layer" :style="backgroundLayerStyle"></div>
    
    <el-card class="article-header" :style="contentCardStyle">
      <!-- 按钮组 -->
      <div class="header-buttons">
        <!-- 返回按钮 -->
        <el-tooltip content="返回首页" placement="top" :enterable="false">
          <el-button 
            class="icon-btn back-btn" 
            type="primary" 
            size="small" 
            circle
            @click="goBack"
          >
            <i class="el-icon-back"></i>
          </el-button>
        </el-tooltip>
        
        <!-- 背景设置按钮 -->
        <background-settings @background-updated="handleBgSettingsUpdated" />
        
        <!-- 显示目录按钮 -->
        <el-tooltip content="文章目录" placement="top" :enterable="false">
          <el-button 
            class="icon-btn show-toc-btn" 
            type="primary" 
            size="small" 
            circle
            @click="toggleNavigation"
          >
            <i class="el-icon-menu"></i>
          </el-button>
        </el-tooltip>
      </div>
      
      <!-- PC端头部内容 -->
      <div class="header-content pc-only" v-if="!isMobile">
        <div class="title-row">
          <el-tag
            :color="getArticleTypeStyle(article.article_type)"
            effect="dark"
            class="article-type"
          >
            {{ article.article_type }}
          </el-tag>
          <h2 class="article-title">{{ article.article_name }}</h2>
        </div>

        <div class="time-info">
          <span>
            <i class="el-icon-date"></i>
            {{ article.create_time }}
          </span>
          <span class="divider">|</span>
          <template v-if="article.update_time">
            <span class="update-time">
              <i class="el-icon-refresh"></i>
              {{ article.update_time }}
            </span>
            <span class="divider">|</span>
          </template>
          <span class="read-count">
            <i class="el-icon-view"></i>
            {{ readCount }}
          </span>
          <span class="divider">|</span>
          <div class="article-tags">
            <el-tag
              v-for="tag in articleTags"
              :key="tag"
              size="small"
              class="article-tag"
            >
              {{ tag }}
            </el-tag>
          </div>
        </div>
      </div>
      
      <!-- 移动端头部内容 -->
      <div class="header-content mobile-only" v-if="isMobile">
        <!-- 第一行：文章标题 -->
        <h2 class="article-title" style="margin-bottom: 10px; font-size: 20px;">{{ article.article_name }}</h2>
        
        <!-- 第二行：创建时间 -->
        <div class="time-item" style="padding-top: 5px;">
          <i class="el-icon-date"></i>
          <span>{{ article.create_time }}</span>
        </div>
        
        <!-- 第三行：更新时间（如果有） -->
        <div v-if="article.update_time" class="time-item">
          <i class="el-icon-refresh"></i>
          <span>{{ article.update_time }}</span>
        </div>
        
        <!-- 第四行：文章类型、标签、阅读量 -->
        <div class="meta-row">
          <div class="left-items">
            <!-- 文章类型 -->
            <el-tag
              :color="getArticleTypeStyle(article.article_type)"
              effect="dark"
              class="article-type"
            >
              {{ article.article_type }}
            </el-tag>
            
            <!-- 文章标签 -->
            <el-tag
              v-for="tag in articleTags"
              :key="tag"
              size="small"
              class="article-tag"
            >
              {{ tag }}
            </el-tag>
          </div>
          
          <!-- 阅读量 -->
          <span class="read-count" style="padding-right: 0;">
            <i class="el-icon-view"></i>
            {{ readCount }}
          </span>
        </div>
      </div>
    </el-card>

    <!-- 内容区域容器 -->
    <div class="content-container">
      <!-- 文章内容区域 -->
      <div class="main-content-wrapper">
        <!-- 文章内容 -->
        <el-card class="article-content" :class="{ 'no-navigation': !showNavigation }" :style="contentCardStyle">
          <!-- 加密文章锁定状态 -->
          <div v-if="isLocked" class="locked-content">
            <i class="el-icon-lock locked-icon"></i>
            <p class="locked-title">这篇文章已加密</p>
            <p class="locked-desc">请输入密码查看内容</p>
            <div class="locked-pwd-form">
              <el-input
                v-model="pwdInput"
                type="password"
                placeholder="请输入密码"
                @keydown.enter.native="handlePwdSubmit"
                style="margin-bottom: 10px; width: 200px;"
              />
              <el-button type="primary" size="small" @click="handlePwdSubmit">确定</el-button>
            </div>
            <p v-if="pwdErrorMsg" class="locked-error">{{ pwdErrorMsg }}</p>
          </div>
          <!-- 自定义渲染容器 -->
          <div v-else class="custom-markdown-container">
            <div
              ref="markdownDisplay"
              class="markdown-display"
              @click="handleContentClick"
            >
              <div class="markdown-content" v-html="renderedContent"></div>
              <!-- 版权信息 - 紧跟在内容之后 -->
              <div class="copyright-info" ref="copyrightInfo">
                <span class="pc-copyright">Copyright © 2024 OIer之路 All rights reserved. ｜ 闽ICP备2025095302号-1</span>
                <div class="mobile-copyright">
                  <div>Copyright © 2024 OIer之路 All rights reserved.</div>
                  <div>闽ICP备2025095302号-1</div>
                </div>
              </div>
            </div>
          </div>
        </el-card>
        
        <!-- 导航区域 - 独立卡片 -->
        <div v-show="showNavigation && !isMobile" class="navigation-card" :style="navigationCardStyle">
          <div class="navigation-header">
            <div class="title-wrapper">
              <span>文章目录</span>
            </div>
              <i class="el-icon-close" @click="toggleNavigation"></i>
          </div>
          <div class="navigation-content">
            <div 
              v-for="(item, index) in navigationItems" 
              :key="index"
              class="navigation-item"
              :class="{ active: currentHeadingId === item.id }"
              :style="{ paddingLeft: (item.level - 1) * 15 + 20 + 'px' }"
              @click="scrollToHeading(item.id)"
            >
              {{ item.text }}
            </div>
            <div v-if="navigationItems.length === 0" class="navigation-empty">
              无目录内容
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 移动端浮动目录 -->
    <div v-if="isMobile && showNavigation" class="mobile-navigation-overlay" @click="toggleNavigation">
      <div class="mobile-navigation-card" :style="contentCardStyle" @click.stop>
        <div class="mobile-navigation-header">
          <div class="title-wrapper">
            <span>文章目录</span>
          </div>
          <i class="el-icon-close" @click="toggleNavigation"></i>
        </div>
        <div class="mobile-navigation-content">
          <div 
            v-for="(item, index) in navigationItems" 
            :key="index"
            class="mobile-navigation-item"
            :class="{ active: currentHeadingId === item.id }"
            :style="{ paddingLeft: (item.level - 1) * 15 + 15 + 'px' }"
            @click="scrollToHeadingMobile(item.id)"
          >
            {{ item.text }}
          </div>
          <div v-if="navigationItems.length === 0" class="mobile-navigation-empty">
            无目录内容
          </div>
        </div>
      </div>
    </div>
    
    <!-- 移动端按钮组，固定在右下角垂直排列 -->
    <div v-if="isMobile" class="mobile-action-buttons">
      <div class="mobile-button-row">
      <!-- 返回按钮 -->
      <el-button 
        class="mobile-icon-btn" 
        @click="goBack"
      >
        <i class="el-icon-back"></i>
      </el-button>
      </div>
      
      <div class="mobile-button-row">
      <!-- 显示目录按钮 -->
      <el-button 
        class="mobile-icon-btn" 
        @click="toggleNavigation"
      >
        <i class="el-icon-menu"></i>
      </el-button>
      </div>

      <div class="mobile-button-row">
      <!-- 背景设置按钮 -->
      <div class="mobile-bg-settings-wrapper">
        <background-settings @background-updated="handleBgSettingsUpdated" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getBlogArticle, increaseArticleReadCount, verifyArticlePassword } from '@/api/article'
import * as marked from 'marked'
import BackgroundSettings from '@/components/BackgroundSettings.vue'

export default {
  name: 'BlogArticle',
  components: {
    BackgroundSettings
  },
  data() {
    return {
      article: {},
      renderedContent: '',
      navigationItems: [],
      currentHeadingId: '',
      showNavigation: true,
      readCount: 0,
      isMobile: false,
      contentCardStyle: { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
      navigationCardStyle: { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
      backgroundLayerStyle: {},
      backgroundStyle: { backgroundColor: '#f0f2f5' },
      scrollObserver: null,
      scrollFixDebounceTimer: null,
      domObserver: null,
      copyrightGuardianTimer: null,
      // 加密文章相关
      isLocked: false,
      pwdInput: '',
      pwdErrorMsg: '',
      pwdErrorCount: 0,
      currentEncryptedCode: null
    }
  },
  computed: {
    articleTags() {
      return this.article.article_tag ? this.article.article_tag.split(',') : []
    }
  },
  watch: {
    // 监听路由变化，重新加载背景设置
    '$route'(to, from) {
      if (to.params.articleCode !== from.params.articleCode) {
        // 如果文章ID变化，重新加载文章
        this.getDetail(to.params.articleCode);
      }
    }
  },
  created() {
    // 加载文章
    if (this.$route.params.articleCode) {
      this.getDetail(this.$route.params.articleCode)
    }
    
    // 检测设备类型
    this.checkDeviceType()
    
    // 根据设备类型设置目录默认显示状态
    this.showNavigation = !this.isMobile
  },
  mounted() {
    // 确保隐藏App.vue中的全局移动端菜单按钮
    this.hideGlobalMobileMenu()
    
    // 加载必要的脚本
    this.loadMathJax()
    
    // 初始化滚动监听
    this.initScrollObserver()
    
    // 添加全局样式，确保行内代码样式正确显示
    this.addGlobalStyles()
    
    // 监听storage变化，以便在其他页面修改设置时同步更新
    window.addEventListener('storage', this.handleStorageChange)
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
    
    // 修复移动端滚动问题 - 初始化
    this.$nextTick(() => {
      // 确保版权信息显示
      this.checkCopyrightInfo();
      
      // 只在移动端调用fixMobileScroll
      if (this.isMobile) {
        this.fixMobileScroll();
      }
    })
    
    // 在页面完全加载后再次调用
    window.addEventListener('load', () => {
      this.checkCopyrightInfo();
      if (this.isMobile) {
        this.fixMobileScroll();
      }
    }, { once: true })
    
    // 监听页面可见性变化
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // 添加DOM观察器来监听文章内容区域变化
    this.$nextTick(() => {
      // 确保版权信息初始显示
      this.checkCopyrightInfo();
      
      // 添加DOM变化观察器
      if (this.$refs.markdownDisplay) {
        // 创建观察器配置
        const config = { 
          childList: true,  // 监听子节点变化
          subtree: true,    // 监听所有后代节点变化
          attributes: true  // 监听属性变化
        };
        
        // 创建观察器回调
        const callback = (mutationsList, observer) => {
          // 内容变化时检查版权信息
          this.checkCopyrightInfo();
        };
        
        // 创建观察器并开始监听
        const observer = new MutationObserver(callback);
        observer.observe(this.$refs.markdownDisplay, config);
        
        // 保存观察器引用以便后续清理
        this.domObserver = observer;
      }
    });
    
    // 处理首次渲染
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        this.checkCopyrightInfo();
        if (this.isMobile) {
          this.fixMobileScroll();
        }
      }, 300);
    }, { once: true });
    
    // 在内容首次渲染后检查版权信息
    setTimeout(() => {
      this.checkCopyrightInfo();
      if (this.isMobile) {
        this.fixMobileScroll();
      }
    }, 500);
  },
  beforeDestroy() {
    // 清理滚动监听
    if (this.scrollObserver) {
      this.scrollObserver.disconnect()
    }
    
    // DOM观察器清理
    if (this.domObserver) {
      this.domObserver.disconnect()
    }
    
    // 清理防抖计时器
    if (this.scrollFixDebounceTimer) {
      clearTimeout(this.scrollFixDebounceTimer)
    }
    
    // 清理版权信息守护机制
    if (this.copyrightGuardianTimer) {
      clearInterval(this.copyrightGuardianTimer)
    }
    
    // 移除全局样式
    this.removeGlobalStyles()
    
    // 移除storage事件监听
    window.removeEventListener('storage', this.handleStorageChange)
    
    // 移除窗口大小监听
    window.removeEventListener('resize', this.handleResize)
    
    // 移除load事件监听
    window.removeEventListener('load', this.fixMobileScroll)
    
    // 移除页面可见性监听
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    // 移除方向变化监听器
    if (this.orientationHandler && window.screen && window.screen.orientation) {
      window.screen.orientation.removeEventListener('change', this.orientationHandler)
    }
    
    // 恢复App.vue中的全局移动端菜单按钮显示
    this.restoreGlobalMobileMenu()
  },
  activated() {
    // 页面被重新激活时（比如从缓存中恢复）检查版权信息
    this.$nextTick(() => {
      this.checkCopyrightInfo();
      
      // 只在移动端调用fixMobileScroll
      if (this.isMobile) {
        this.fixMobileScroll();
      }
    });
    
    // 确保隐藏App.vue中的全局移动端菜单按钮
    this.hideGlobalMobileMenu();
  },
  methods: {
    // 恢复App.vue中的全局移动端菜单按钮显示
    restoreGlobalMobileMenu() {
      const globalMobileMenuBtn = document.querySelector('#app > .mobile-menu-btn')
      if (globalMobileMenuBtn) {
        // 只在移动端视图下恢复显示
        if (window.innerWidth <= 768) {
          globalMobileMenuBtn.style.display = 'flex'
        }
      }
    },
    
    // 隐藏App.vue中的全局移动端菜单按钮
    hideGlobalMobileMenu() {
      const globalMobileMenuBtn = document.querySelector('#app > .mobile-menu-btn')
      if (globalMobileMenuBtn) {
        globalMobileMenuBtn.style.display = 'none'
      }
    },
    
    // 添加全局样式
    addGlobalStyles() {
      if (document.getElementById('article-global-styles')) return
      const styleEl = document.createElement('style')
      styleEl.id = 'article-global-styles'
      styleEl.textContent = `
        .markdown-display p code {
          padding: 0.2em 0.4em;
          margin: 0 0.2em;
          background-color: #f6f8fa;
          color: #476582;
          border-radius: 3px;
          font-size: 0.9em;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          white-space: nowrap;
        }

        .markdown-display li code {
          padding: 0.2em 0.4em;
          margin: 0 0.2em;
          background-color: #f6f8fa;
          color: #476582;
          border-radius: 3px;
          font-size: 0.9em;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          white-space: nowrap;
        }

        /* 彩色行间公式特殊处理 - 确保与普通公式一致 */
        .display-math-colored {
          display: block;
          text-align: center;
          width: 100%;
          margin: 1em 0;
          padding: 0.5em 0;
          /* 与普通公式保持一致的样式 */
          background: transparent;
          border-radius: 0;
          border-bottom: none;
        }
        
        /* 暗色模式下的彩色公式 - 也移除背景色 */
        @media (prefers-color-scheme: dark) {
          .display-math-colored {
            /*background-color: #1e1e1e;*/
          }
        }
        
        .display-math-colored .MathJax_SVG_Display,
        .display-math-colored .MathJax_Display {
          margin: 0 !important;
          padding: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
        }
        
        /* 代码块样式 */
        .markdown-display pre {
          background-color: #0d1117;
          padding: 0;
          border-radius: 6px;
          overflow: hidden;
          position: relative;
          width: 90%;
          max-width: 900px;
          margin: 16px 0;
          border: 1px solid #30363d;
          display: flex;
          flex-direction: column;
        }
        
        /* 移动端代码块样式 */
        @media screen and (max-width: 768px) {
          .markdown-display pre {
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            box-sizing: border-box !important;
            border-left: none !important;
            border-right: none !important;
            border-radius: 0 !important; /* 移除圆角 */
          }
          
          .markdown-display pre code {
            padding-left: 50px !important; /* 保持左侧行号区域的空间 */
            width: 100% !important;
            box-sizing: border-box !important;
          }
          
          /* 修复代码容器样式 */
          .markdown-display pre .code-container {
            width: 100% !important;
            box-sizing: border-box !important;
          }
          
          /* 确保行号显示正确 */
          .markdown-display pre .line-numbers {
            left: 0 !important;
            z-index: 2 !important;
          }
        }
        
        .markdown-display pre code {
          color: #c9d1d9;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          font-size: 14px;
          line-height: 1.5;
          display: block;
          padding: 16px 16px 16px 16px;
          overflow-x: auto;
          tab-size: 4;
          white-space: pre;
        }
        
        /* 移动端代码块内容样式 */
        @media screen and (max-width: 768px) {
          .markdown-display pre code {
            padding-left: 50px; /* 保持左侧行号区域的空间 */
          }
          
          /* 修复代码容器样式 */
          .markdown-display pre .code-container {
            width: 100% !important;
            box-sizing: border-box !important;
            margin-left: 0 !important; /* 移除左侧间距 */
            padding-left: 0 !important;
            overflow-x: visible !important; /* 防止水平滚动 */
          }
          
          /* 确保行号显示正确 */
          .markdown-display pre .line-numbers {
            left: 0 !important;
            z-index: 2 !important;
            width: 40px !important;
          }
        }
        
        /* 代码容器样式 */
        .markdown-display pre .code-container {
          flex: 1;
          overflow-x: auto;
          padding-left: 0; /* 移除左侧内边距 */
          position: relative;
        }
        
        /* 行号样式 */
        .markdown-display pre .line-numbers {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          padding: 16px 8px;
          color: #6e7681;
          text-align: right;
          background-color: #161b22;
          border-right: 1px solid #30363d;
          user-select: none;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          white-space: pre;
          line-height: 1.5;
          font-size: 14px;
          width: 40px; /* 固定宽度 */
          box-sizing: border-box;
          z-index: 1;
        }
        
        .markdown-display pre .copy-code-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          padding: 4px 8px;
          background: rgba(110, 118, 129, 0.4);
          color: #c9d1d9;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.2s;
          z-index: 10;
        }
        
        .markdown-display pre:hover .copy-code-btn {
          opacity: 1;
        }
        
        .markdown-display pre .copy-code-btn:hover {
          background: rgba(110, 118, 129, 0.6);
        }
        
        /* 表格样式修复 */
        .markdown-display table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
          display: block;
          overflow-x: auto;
        }
        
        .markdown-display table th,
        .markdown-display table td {
          padding: 8px 12px;
          border: 1px solid #dfe2e5;
        }
        
        .markdown-display table th {
          background-color: #f6f8fa;
          font-weight: 600;
        }
        
        .markdown-display table tr:nth-child(2n) {
          background-color: #f8f8f8;
        }
        
        /* 自定义highlight.js样式调整 */
        .hljs-keyword {
          color: #ff7b72 !important;
        }
        
        .hljs-built_in {
          color: #79c0ff !important;
        }
        
        .hljs-type {
          color: #ffa657 !important;
        }
        
        .hljs-string {
          color: #a5d6ff !important;
        }
        
        .hljs-number {
          color: #d2a8ff !important;
        }
        
        .hljs-comment {
          color: #8b949e !important;
          font-style: italic !important;
        }
        
        .hljs-function {
          color: #d2a8ff !important;
        }
        
        .hljs-title {
          color: #79c0ff !important;
        }
        
        .hljs-title.function_ {
          color: #d2a8ff !important;
        }
        
        .hljs-title.class_ {
          color: #ffa657 !important;
        }
        
        .hljs-params {
          color: #c9d1d9 !important;
        }
        
        .hljs-operator {
          color: #ff7b72 !important;
        }
        
        .hljs-punctuation {
          color: #c9d1d9 !important;
        }
        
        .hljs-property {
          color: #79c0ff !important;
        }
        
        .hljs-tag {
          color: #7ee787 !important;
        }
        
        .hljs-attr {
          color: #d2a8ff !important;
        }
        
        .hljs-name {
          color: #7ee787 !important;
        }

        .hljs-variable {
          color: #ffa657 !important;
        }
        
        .hljs-template-variable {
          color: #ffa657 !important;
        }
        
        .hljs-literal {
          color: #0550ae !important;
        }
        
        .hljs-meta {
          color: #8b949e !important;
        }
        
        .hljs-selector {
          color: #d2a8ff !important;
        }
        
        .hljs-char {
          color: #a5d6ff !important;
        }
        
        .hljs-doctag {
          color: #8b949e !important;
          font-style: italic !important;
        }
        
        .hljs-addition {
          color: #aff5b4 !important;
          background-color: #033a16 !important;
        }
        
        .hljs-deletion {
          color: #ffdcd7 !important;
          background-color: #67060c !important;
        }
        
        .hljs-emphasis {
          font-style: italic !important;
        }
        
        .hljs-strong {
          font-weight: bold !important;
        }
        
        /* 数学公式样式 */
        .markdown-display .MathJax_Display,
        .markdown-display .MathJax_SVG_Display,
        .markdown-display .mjx-chtml.MJXc-display {
          overflow-x: auto !important;
          overflow-y: hidden !important;
          margin: 1em 0 !important;
          padding: 0.5em 0 !important;
          background: transparent !important; /* 移除背景色 */
          border-radius: 0 !important; /* 移除边框圆角 */
          box-shadow: none !important; /* 移除阴影 */
          text-align: center !important;
          display: block !important;
          width: 100% !important;
          position: relative !important;
          border-bottom: none !important; /* 移除底部横线 */
        }
        
        /* 实际公式元素 */
        .markdown-display .MathJax_Display .MathJax,
        .markdown-display .MathJax_SVG_Display .MathJax_SVG {
          cursor: pointer !important;
          display: inline-block !important;
          margin: 0 auto !important;
        }
        
        /* 改进行间公式容器样式 */
        .MathJax_SVG_Display, .MathJax_Display {
          display: block !important;
          text-align: center !important;
          margin: 1em 0 !important;
          background: transparent !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          border-bottom: none !important;
        }
        
        /* 彩色行间公式样式 - 特殊处理 */
        .MathJax_SVG[style*="color:red"], .MathJax[style*="color:red"],
        .MathJax_SVG[style*="color:blue"], .MathJax[style*="color:blue"],
        .MathJax_SVG[style*="color:green"], .MathJax[style*="color:green"],
        .MathJax_SVG[style*="color:purple"], .MathJax[style*="color:purple"],
        .MathJax_SVG[style*="color:orange"], .MathJax[style*="color:orange"] {
          display: inline-block !important;
          margin: 0 auto !important;
        }
        
        /* 强制行间公式容器样式 */
        .markdown-display .MathJax_SVG_Display, .markdown-display .MathJax_Display {
          display: block !important;
          text-align: center !important;
          width: 100% !important;
          margin: 1em 0 !important;
          position: relative !important;
          background: transparent !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          border-bottom: none !important;
        }
        
        /* 引用样式 */
        .markdown-display blockquote {
          margin: 20px 0 !important;
          padding: 15px 20px 15px 35px !important;
          color: #476582 !important;
          background-color: rgba(235, 245, 255, 0.7) !important;
          border-left: 4px solid #409EFF !important;
          border-radius: 0 4px 4px 0 !important;
          position: relative !important;
          display: block !important;
          box-sizing: border-box !important;
        }
        
        /* 引号图标 */
        .markdown-display blockquote::before {
          content: '"';
          position: absolute !important;
          left: 10px !important;
          top: 8px !important;
          font-size: 20px !important;
          color: #409EFF !important;
          opacity: 0.5 !important;
        }
        
        /* 确保段落样式正确 */
        .markdown-display blockquote p {
          margin: 8px 0 !important;
          line-height: 1.6 !important;
          color: #476582 !important;
        }
        
        /* 任务列表样式 - 隐藏列表标记，只显示复选框 */
        .markdown-display ul.task-list {
          list-style: none !important;
          padding-left: 20px !important; /* 任务列表左缩进设置为20px */
          margin-top: 0 !important;
          margin-bottom: 0 !important;
          margin-block-start: 0 !important;
          margin-block-end: 0 !important;
          
          li.task-list-item {
            list-style: none !important;
            display: flex;
            flex-wrap: wrap; /* 允许子列表换行 */
            align-items: center;
            
            /* 隐藏默认的列表标记 */
            &::before {
              display: none !important;
            }
            
            /* 任务列表复选框样式 */
            input[type="checkbox"] {
              margin: 0 8px 0 0;
              flex-shrink: 0;
              transform: scale(1);
              vertical-align: middle;
              position: relative;
              top: -1px;
            }
            
            /* 任务列表文本内容 */
            > span, > p {
              margin: 0;
              flex: 1;
              line-height: 1.5;
              display: inline-block;
              vertical-align: middle;
            }

            /* 子级列表换行并缩进 */
            > ul, > ol {
              display: block;
              width: 100%;
              flex-basis: 100%;
              margin: 4px 0 0 0;
              padding-left: 1.5em;
            }
          }
        }
        
        /* 兼容性：处理没有task-list类的任务列表 */
        .markdown-display ul li input[type="checkbox"] {
          margin: 0 8px 0 0;
          transform: scale(1);
          vertical-align: middle;
          position: relative;
          top: -1px;
        }
        
        /* 针对包含复选框的列表项，隐藏列表标记 */
        .markdown-display ul li:has(input[type="checkbox"]) {
          list-style: none;
          display: flex;
          align-items: center;
          
          &::before {
            display: none;
          }
        }
        
        /* 强制任务列表缩进 - 最高优先级 */
        .markdown-display ul:has(li input[type="checkbox"]) {
          padding-left: 20px !important;
          list-style: none !important;
        }
        
        /* 备用方案：通过属性选择器 */
        .markdown-display ul[class*="task"] {
          padding-left: 20px !important;
          list-style: none !important;
        }
+
+        /* 当列表中既有任务项又有普通项时，恢复普通项标记，避免小黑点丢失 */
+        .markdown-display ul:has(li input[type="checkbox"]) > li:not(.task-list-item) {
+          list-style: disc !important;
+        }
+        .markdown-display ul.task-list > li:not(.task-list-item) {
+          list-style: disc !important;
+        }
+
+        /* 统一顶层与通用列表的上下间距，避免相邻列表出现默认1em */
+        .markdown-display > .custom-markdown-container > .markdown-display > .markdown-content > ul,
+        .markdown-display > .custom-markdown-container > .markdown-display > .markdown-content > ol {
+          margin-top: 0 !important;
+          margin-bottom: 0 !important;
+          margin-block-start: 0 !important;
+          margin-block-end: 0 !important;
+        }
+        .markdown-display ul,
+        .markdown-display ol {
+          margin-top: 0 !important;
+          margin-bottom: 0 !important;
+          margin-block-start: 0 !important;
+          margin-block-end: 0 !important;
+        }
+        /* 相邻列表间距清零 */
+        .markdown-display ul + ul,
+        .markdown-display ul + ol,
+        .markdown-display ol + ul,
+        .markdown-display ol + ol {
+          margin-top: 0 !important;
+        }
+        /* 段落与列表相邻时折叠段落外边距 */
+        .markdown-display p:has(+ ul),
+        .markdown-display p:has(+ ol),
+        .markdown-display p:has(+ ul.task-list) {
+          margin-top: 0 !important;
+          margin-bottom: 0 !important;
+        }
+        .markdown-display ul + p,
+        .markdown-display ol + p {
+          margin-top: 0 !important;
+          margin-bottom: 0 !important;
+        }
+        
+        /* 空段落清零外边距，避免空行导致的1em间距 */
+        .markdown-display p:empty {
+          margin-top: 0 !important;
+          margin-bottom: 0 !important;
+          margin-block-start: 0 !important;
+          margin-block-end: 0 !important;
+        }
+        
+        /* 仅包含空白字符或换行的段落也清零 */
+        .markdown-display p:has(br:only-child) {
+          margin-top: 0 !important;
+          margin-bottom: 0 !important;
+          margin-block-start: 0 !important;
+          margin-block-end: 0 !important;
+        }
+        
+        /* 专门针对任务列表后无序列表的p标签 - 最高优先级强制清零 */
+        .markdown-display ul:not(.task-list) li > p {
+          margin: 0 !important;
+          margin-top: 0 !important;
+          margin-bottom: 0 !important;
+          margin-block-start: 0 !important;
+          margin-block-end: 0 !important;
+          margin-inline-start: 0 !important;
+          margin-inline-end: 0 !important;
+        }
+
+        /* 图片最大宽度不超出内容区域 */
+        .markdown-content img,
+        .markdown-display .markdown-content img,
+        .markdown-display img,
+        .markdown-content p img {
+          max-width: 100% !important;
+          height: auto !important;
+          width: auto !important;
+          display: block !important;
+        }

+        /* 自定义容器样式 */
+        .custom-container {
+          margin: 16px 0 !important;
+          border-radius: 6px !important;
+          overflow: hidden !important;
+          border: 1px solid !important;
+        }
+        .container-header {
+          display: flex !important;
+          align-items: center !important;
+          gap: 8px !important;
+          padding: 10px 14px !important;
+          cursor: pointer !important;
+          user-select: none !important;
+        }
+        .container-icon { font-size: 16px !important; }
+        .container-title { font-weight: normal !important; font-size: 14px !important; color: #303133 !important; flex: 1 !important; }
+        .container-toggle { font-size: 12px !important; color: #909399 !important; transition: transform 0.2s !important; }
+        .custom-container.collapsed .container-content { display: none; }
+        .custom-container:not(.collapsed) .container-toggle { transform: rotate(90deg); }
+        .container-content {
+          padding: 0 14px 12px !important;
+          font-size: 14px !important;
+          line-height: 1.7 !important;
+          color: #2c3e50 !important;
+        }
+        @media (prefers-color-scheme: dark) {
+          .container-title { color: #e0e0e0 !important; }
+          .container-content { color: #c9d1d9 !important; }
+        }
+      }
      `
      document.head.appendChild(styleEl)
    },
    
    // 移除全局样式
    removeGlobalStyles() {
      const styleEl = document.getElementById('article-global-styles')
      if (styleEl) {
        styleEl.parentNode.removeChild(styleEl)
      }
    },
    
    // 加载MathJax
    loadMathJax() {
      // 清除可能存在的旧MathJax实例
      if (window.MathJax) {
        window.MathJax = null;
      }
      
      // 加载MathJax配置
      const mathjaxConfig = document.createElement('script');
      mathjaxConfig.type = 'text/x-mathjax-config';
      mathjaxConfig.textContent = `
        MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$','$']],
            displayMath: [['$$','$$']],
            processEscapes: true,
            skipTags: ['script', 'noscript', 'style', 'textarea', 'pre']
          },
          messageStyle: 'none',
          showProcessingMessages: false,
          jax: ["input/TeX", "output/SVG"],
          extensions: ["tex2jax.js", "MathZoom.js", "MathMenu.js", "toMathML.js", "TeX/noErrors.js", "TeX/noUndefined.js", "TeX/AMSmath.js", "TeX/AMSsymbols.js", "TeX/color.js"],
          TeX: { 
            extensions: ["AMSmath.js", "AMSsymbols.js", "color.js"],
            Macros: {
              red: ["{\\\\color{red}#1}", 1],
              blue: ["{\\\\color{blue}#1}", 1],
              green: ["{\\\\color{green}#1}", 1],
              purple: ["{\\\\color{purple}#1}", 1],
              orange: ["{\\\\color{orange}#1}", 1]
            }
          },
          SVG: {
            scale: 110,
            linebreaks: { automatic: true }
          },
          "HTML-CSS": {
            availableFonts: ["TeX"],
            scale: 110
          },
          // 禁用点击缩放功能，避免与自定义缩放功能冲突
          menuSettings: {
            zoom: "None",
            showRenderer: false,
            showLocale: false,
            showFontMenu: false
          },
          MathMenu: {
            showRenderer: false,
            showLocale: false,
            showFontMenu: false
          },
          MathZoom: {
            delay: 600,
            styles: {
              "#MathJax_Zoom": {
                "background-color": "#FFFFFF",
                "box-shadow": "0px 0px 15px #888888",
                "border": "1px solid #CCCCCC",
                "padding": "10px",
                "max-width": "90%",
                "margin": "0 auto",
                "z-index": "9999",
                "position": "fixed",
                "left": "0",
                "right": "0",
                "top": "50px",
                "overflow": "auto",
                "max-height": "80vh"
              }
            }
          }
        });
        
        // 添加特殊处理，确保行间公式正确居中显示
        MathJax.Hub.Register.StartupHook("End", function () {
          // 找到所有行间公式容器
          const displayMathContainers = document.querySelectorAll('.MathJax_SVG_Display, .MathJax_Display');
          displayMathContainers.forEach(container => {
            // 应用居中样式
            container.style.display = 'block';
            container.style.textAlign = 'center';
            
            // 确保位于单独行
            const parentEl = container.parentNode;
            if (parentEl && parentEl.tagName.toLowerCase() !== 'div' && parentEl.tagName.toLowerCase() !== 'p') {
              // 如果公式不在段落或块级元素内，则包装它
              const wrapper = document.createElement('div');
              wrapper.style.display = 'block';
              wrapper.style.textAlign = 'center';
              wrapper.style.margin = '1em 0';
              parentEl.insertBefore(wrapper, container);
              wrapper.appendChild(container);
            }
          });
        });
      `;
      
      document.head.appendChild(mathjaxConfig);
      
      // 直接从CDN加载MathJax
      const cdnScript = document.createElement('script');
      cdnScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.9/MathJax.js?config=TeX-AMS_SVG';
      cdnScript.async = true;
      cdnScript.crossOrigin = "anonymous";
      
      cdnScript.onload = () => {
        console.log('CDN MathJax加载成功');
        this.initMathJax();
      };
      
      cdnScript.onerror = (err) => {
        console.error('主要CDN加载失败，尝试备用CDN', err);
        
        // 尝试备用CDN
        const backupScript = document.createElement('script');
        backupScript.src = 'https://cdn.jsdelivr.net/npm/mathjax@2.7.9/MathJax.js?config=TeX-AMS_SVG';
        backupScript.async = true;
        backupScript.crossOrigin = "anonymous";
        
        backupScript.onload = () => {
          console.log('备用CDN MathJax加载成功');
          this.initMathJax();
        };
        
        backupScript.onerror = (backupErr) => {
          console.error('所有MathJax加载尝试均失败', backupErr);
          alert('加载数学公式渲染引擎失败，请刷新页面重试');
        };
        
        document.head.appendChild(backupScript);
      };
      
      document.head.appendChild(cdnScript);
      
      // 监听MathJax渲染完成事件，只在这里初始化代码块
      document.addEventListener('mathjax-rendered', () => {
        console.log('MathJax渲染完成事件触发，初始化代码块');
        
        // 清除所有已有的代码块增强元素，避免重复添加
        this.cleanupCodeBlocks();
        
        // 然后初始化代码块
        this.initCodeBlocks();
      }, { once: true });
    },
    
    // 初始化MathJax并渲染公式
    initMathJax() {
      if (window.MathJax) {
        setTimeout(() => {
          try {
            console.log('开始渲染LaTeX公式');
            
            // 确保MathJax完全加载
            if (!window.MathJax.Hub) {
              console.warn('MathJax.Hub不可用，等待MathJax完全加载');
              setTimeout(this.initMathJax.bind(this), 500);
              return;
            }
            
            // 确保SVG输出器已加载
            if (!window.MathJax.OutputJax.SVG) {
              console.warn('MathJax SVG输出器未加载，等待加载完成');
              setTimeout(this.initMathJax.bind(this), 500);
              return;
            }
            
            // 清理可能存在的错误状态
            const elements = document.querySelectorAll('.MathJax_Error');
            elements.forEach(el => el.parentNode.removeChild(el));
            
            // 开始处理公式
            window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, this.$refs.markdownDisplay]);
            
            // 处理颜色公式（使用安全的方式添加回调）
            const self = this;
            window.MathJax.Hub.Queue(function() {
              try {
                self.enhanceMathColors();
              } catch (e) {
                console.error('增强颜色处理失败:', e);
              }
              
              // 触发渲染完成事件
              try {
                const event = new CustomEvent('mathjax-rendered');
                document.dispatchEvent(event);
              } catch (e) {
                console.error('触发事件失败:', e);
              }
            });
          } catch (e) {
            console.error('MathJax渲染失败', e);
          }
        }, 500);
      } else {
        console.warn('MathJax对象不可用');
        // 等待500ms后重试
        setTimeout(this.initMathJax.bind(this), 500);
      }
    },
    
    // 增强数学公式中的颜色
    enhanceMathColors() {
      try {
        console.log('增强数学公式颜色');
        
        // 检查页面中是否有MathJax元素
        const mathJaxElements = document.querySelectorAll('.MathJax_SVG, .MathJax');
        console.log(`发现${mathJaxElements.length}个MathJax元素`);
        
        // 处理特殊的math-tex类
        const mathTexElements = document.querySelectorAll('.math-tex');
        mathTexElements.forEach(el => {
          // 如果已经处理过，跳过
          if (el.dataset.processed) return;
          
          const parentStyle = el.parentElement.style.color;
          if (parentStyle) {
            // 找到包含此元素的MathJax元素并应用颜色
            const mathJaxEl = el.closest('.MathJax_SVG, .MathJax');
            if (mathJaxEl) {
              const mathElems = mathJaxEl.querySelectorAll('.mi, .mo, .mn');
              mathElems.forEach(mathElem => {
                mathElem.style.color = parentStyle;
              });
            }
          }
          
          // 标记为已处理
          el.dataset.processed = 'true';
        });
        
        // 检查所有双美元符号公式
        const displayMathElements = document.querySelectorAll('.MathJax_SVG_Display, .MathJax_Display');
        console.log(`发现${displayMathElements.length}个行间公式`);
        
        // 确保行间公式正确渲染
        displayMathElements.forEach(container => {
          // 确保显示为块级元素
          container.style.display = 'block';
          container.style.textAlign = 'center';
          container.style.width = '100%';
          container.style.margin = '1em 0';
          
          // 处理容器内的公式
          const mathElement = container.querySelector('.MathJax, .MathJax_SVG');
          if (mathElement) {
            // 确保公式居中
            mathElement.style.margin = '0 auto';
            
            // 检查是否包含颜色样式
            if (mathElement.getAttribute('style') && 
                mathElement.getAttribute('style').includes('color:')) {
              console.log('发现彩色公式，应用特殊处理');
              container.dataset.colorFormula = 'true';
            }
          }
          
          // 确保容器是块级元素
          const parent = container.parentElement;
          if (parent && parent.style) {
            // 检查父元素是否为行内元素
            const computedStyle = window.getComputedStyle(parent);
            if (computedStyle.display === 'inline' || computedStyle.display === 'inline-block') {
              // 将父元素改为块级元素
              parent.style.display = 'block';
              parent.style.textAlign = 'center';
              parent.style.width = '100%';
              // 检查是否在列表项内，如果是则不设置margin
              const isInListItem = parent.closest('li');
              if (!isInListItem) {
                parent.style.margin = '1em 0';
              }
            }
          }
        });
        
        // 直接用DOM操作修复颜色显示问题
        document.querySelectorAll('.mjx-math [style*="color:"]').forEach(el => {
          console.log('发现带颜色的数学元素:', el.outerHTML.substring(0, 50));
        });
        
        // 添加点击放大功能
        this.addZoomCapability();
        
        // 给所有双美元符号的公式添加额外的样式强制
        setTimeout(() => {
          const displayMathContainers = document.querySelectorAll('.MathJax_SVG_Display, .MathJax_Display');
          displayMathContainers.forEach(container => {
            if (!container.classList.contains('enhanced')) {
              container.classList.add('enhanced');
              container.style.display = 'block';
              container.style.textAlign = 'center';
              container.style.margin = '1em 0';
              container.style.width = '100%';
              
              // 强制父元素为块级
              let parent = container.parentElement;
              if (parent && parent.tagName.toLowerCase() !== 'div' && 
                  parent.tagName.toLowerCase() !== 'p') {
                              // 创建一个新的div元素
              const wrapper = document.createElement('div');
              wrapper.style.display = 'block';
              wrapper.style.width = '100%';
              wrapper.style.textAlign = 'center';
              // 检查是否在列表项内，如果是则不设置margin
              const isInListItem = parent.closest('li');
              if (!isInListItem) {
                wrapper.style.margin = '1em 0';
              }
                
                // 替换原来的元素
                parent.insertBefore(wrapper, container);
                wrapper.appendChild(container);
              }
            }
          });
        }, 1000);
      } catch (e) {
        console.error('增强数学颜色时出错:', e);
      }
    },
    
    // 添加公式点击放大功能
    addZoomCapability() {
      try {
        // 绑定处理函数，确保this上下文正确
        const handleMathClick = this.handleMathClick.bind(this);
        
        // 行内公式 - 这些已经是精确的公式元素
        const inlineMath = document.querySelectorAll('.MathJax, .MathJax_SVG');
        inlineMath.forEach(math => {
          if (!math.dataset.zoomable) {
            math.dataset.zoomable = "true";
            math.style.cursor = "pointer";
            math.addEventListener('click', handleMathClick);
          }
        });
        
        // 行间公式 - 特别处理，只为公式本身添加事件
        const displayMathContainers = document.querySelectorAll('.MathJax_Display, .MathJax_SVG_Display');
        displayMathContainers.forEach(container => {
          // 找到容器内的实际公式元素
          const actualMath = container.querySelector('.MathJax, .MathJax_SVG');
          if (actualMath && !actualMath.dataset.zoomable) {
            actualMath.dataset.zoomable = "true";
            actualMath.style.cursor = "pointer";
            
            // 只给实际公式元素添加点击事件，而不是整个容器
            actualMath.addEventListener('click', handleMathClick);
            
            // 防止事件冒泡到容器
            container.addEventListener('click', (e) => {
              // 如果点击的不是公式本身，阻止事件冒泡
              if (e.target !== actualMath && !actualMath.contains(e.target)) {
                e.stopPropagation();
              }
            });
          }
        });
        
        console.log('添加了点击放大功能');
      } catch (e) {
        console.error('添加点击放大功能失败:', e);
      }
    },
    
    // 处理公式点击事件
    handleMathClick(event) {
      try {
        // 阻止事件传播
        event.stopPropagation();
        event.preventDefault();
        
        // 获取点击的MathJax元素
        const mathElement = event.currentTarget;
        
        // 检查是否已经存在放大层
        if (document.querySelector('.custom-math-zoom')) {
          return;
        }
        
        // 获取原公式的位置和尺寸
        const mathRect = mathElement.getBoundingClientRect();
        
        // 创建放大后的容器
        const zoomContainer = document.createElement('div');
        zoomContainer.className = 'custom-math-zoom';
        zoomContainer.style.position = 'absolute';
        zoomContainer.style.zIndex = '9999';
        zoomContainer.style.backgroundColor = 'white';
        zoomContainer.style.padding = '15px';
        zoomContainer.style.borderRadius = '8px';
        zoomContainer.style.boxShadow = '0 0 20px rgba(0,0,0,0.2)';
        zoomContainer.style.overflow = 'auto';
        zoomContainer.style.border = '1px solid #ddd';
        
        // 检测暗色模式
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
          zoomContainer.style.backgroundColor = '#1e1e1e';
          zoomContainer.style.color = '#e0e0e0';
          zoomContainer.style.borderColor = '#444';
        }
        
        // 复制公式内容
        const mathContent = mathElement.cloneNode(true);
        
        // 处理大小，增加缩放效果
        const mathJaxElement = mathContent.querySelector('.MathJax, .MathJax_SVG');
        if (mathJaxElement) {
          mathJaxElement.style.transform = 'scale(1.5)';
          mathJaxElement.style.transformOrigin = 'center';
          mathJaxElement.style.display = 'block';
          mathJaxElement.style.margin = '0 auto';
        }
        
        // 添加关闭按钮
        const closeButton = document.createElement('div');
        closeButton.className = 'math-zoom-close';
        closeButton.innerHTML = '×';
        closeButton.style.position = 'absolute';
        closeButton.style.top = '5px';
        closeButton.style.right = '5px';
        closeButton.style.cursor = 'pointer';
        closeButton.style.fontSize = '20px';
        closeButton.style.fontWeight = 'bold';
        closeButton.style.color = '#666';
        closeButton.style.width = '24px';
        closeButton.style.height = '24px';
        closeButton.style.lineHeight = '24px';
        closeButton.style.textAlign = 'center';
        closeButton.style.borderRadius = '50%';
        closeButton.style.background = 'rgba(0,0,0,0.05)';
        
        // 暗色模式下的关闭按钮样式
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
          closeButton.style.color = '#aaa';
          closeButton.style.background = 'rgba(255,255,255,0.1)';
        }
        
        closeButton.addEventListener('click', (e) => {
          e.stopPropagation();
          document.body.removeChild(overlay);
          document.body.removeChild(zoomContainer);
        });
        
        // 创建半透明遮罩背景
        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.right = '0';
        overlay.style.bottom = '0';
        overlay.style.backgroundColor = 'rgba(0,0,0,0.3)';
        overlay.style.zIndex = '9998';
        
        // 点击遮罩关闭
        overlay.addEventListener('click', () => {
          document.body.removeChild(overlay);
          document.body.removeChild(zoomContainer);
        });
        
        // 组装弹窗
        zoomContainer.appendChild(mathContent);
        zoomContainer.appendChild(closeButton);
        
        // 先添加到DOM以获取尺寸
        document.body.appendChild(zoomContainer);
        
        // 动态调整宽度 - 确保宽度合适
        const minWidth = Math.max(mathRect.width * 1.2, 300);
        const maxWidth = Math.min(window.innerWidth * 0.9, 800);
        const contentWidth = zoomContainer.scrollWidth;
        
        // 设置最终宽度 - 在最小和最大宽度之间取一个合适的值
        const finalWidth = Math.min(Math.max(contentWidth, minWidth), maxWidth);
        zoomContainer.style.width = `${finalWidth}px`;
        
        // 最大高度，避免超出屏幕
        zoomContainer.style.maxHeight = `${window.innerHeight * 0.7}px`;
        
        // 获取调整后的尺寸
        const zoomRect = zoomContainer.getBoundingClientRect();
        
        // 计算位置 - 在原公式上方居中
        let topPosition = mathRect.top - zoomRect.height - 10;
        
        // 如果放不下，就放在下方
        if (topPosition < 10) {
          topPosition = mathRect.bottom + 10;
        }
        
        // 如果下方也放不下，就放在视口中间
        if (topPosition + zoomRect.height > window.innerHeight - 10) {
          topPosition = Math.max(10, (window.innerHeight - zoomRect.height) / 2);
        }
        
        // 水平居中
        const leftPosition = Math.max(10, mathRect.left + (mathRect.width - zoomRect.width) / 2);
        
        // 确保不超出右侧边界
        if (leftPosition + zoomRect.width > window.innerWidth - 10) {
          zoomContainer.style.right = '10px';
        } else {
          zoomContainer.style.left = `${leftPosition}px`;
        }
        
        zoomContainer.style.top = `${topPosition}px`;
        
        // 添加到页面
        document.body.appendChild(overlay);
        // zoomContainer已经添加过了，不需要再次添加
        
        console.log('显示放大的公式在原位置上方');
      } catch (e) {
        console.error('公式放大处理失败:', e);
      }
    },
    
    // 渲染Markdown内容
    renderMarkdown(content) {
      try {
        console.log('开始渲染Markdown内容');

        // 预处理Markdown中的LaTeX颜色命令（必须在容器处理之前，否则会破坏容器HTML结构）
        content = this.preprocessLatexColors(content);

        // 预处理自定义容器
        content = this.preprocessCustomContainers(content);

        // 处理带颜色的行间公式 - 特殊处理
        content = content.replace(/\$\$\\red\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{red}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\blue\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{blue}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\green\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{green}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\purple\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{purple}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\orange\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{orange}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\color\{(\w+)\}\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{$1}{$2}$$\n</div>\n');
        
        // 临时保护LaTeX公式
        const mathExpressions = [];
        let protectedContent = content.replace(/(\$\$[\s\S]*?\$\$|\$[^\$\n]+?\$)/g, function(match) {
          console.log("保护LaTeX公式:", match.substring(0, 20) + (match.length > 20 ? "..." : ""));
          mathExpressions.push(match);
          return `MATH_PLACEHOLDER_${mathExpressions.length - 1}`;
        });
        
        // 配置marked
        const renderer = new marked.Renderer();
        
        // 自定义代码渲染 - 简单返回代码，后续由highlight.js处理
        renderer.code = function(code, language) {
          // 确保HTML标签被转义
          code = code.replace(/</g, '&lt;').replace(/>/g, '&gt;');
          return `<pre><code class="language-${language || 'plaintext'}">${code}</code></pre>`;
        };
        
        // 自定义引用渲染
        renderer.blockquote = function(quote) {
          // 移除<p>标签前后的空格，以避免样式问题
          return `<blockquote class="custom-blockquote">${quote.trim()}</blockquote>`;
        };
        
        // 自定义列表渲染 - 处理任务列表
        renderer.list = function(body, ordered, start) {
          // 检查是否包含任务列表项
          const hasTaskItems = body.includes('input[type="checkbox"]');
          const listType = ordered ? 'ol' : 'ul';
          const startAttr = ordered && start !== 1 ? ` start="${start}"` : '';
          
          if (hasTaskItems) {
            // 为任务列表添加特殊类
            return `<${listType} class="task-list"${startAttr}>\n${body}</${listType}>\n`;
          } else {
            // 普通列表
            return `<${listType}${startAttr}>\n${body}</${listType}>\n`;
          }
        };
        
        // 自定义列表项渲染 - 处理任务列表项
        renderer.listitem = function(text) {
          // 移除可能被包装的<p>标签，避免额外的margin
          text = text.replace(/^<p>(.*?)<\/p>$/s, '$1');
          
          // 检查是否为任务列表项
          if (text.includes('input[type="checkbox"]')) {
            return `<li class="task-list-item">${text}</li>\n`;
          } else {
            return `<li>${text}</li>\n`;
          }
        };

        marked.setOptions({
          renderer: renderer,
          gfm: true,
          breaks: true,
          headerIds: true,
          langPrefix: 'language-'
        });

        // 渲染markdown内容
        let renderedHtml = marked.parse(protectedContent);
        
        // 还原LaTeX公式
        renderedHtml = renderedHtml.replace(/MATH_PLACEHOLDER_(\d+)/g, function(match, index) {
          const formula = mathExpressions[parseInt(index)];
          console.log("还原LaTeX公式:", formula.substring(0, 20) + (formula.length > 20 ? "..." : ""));
          // 确保公式不被HTML转义
          return formula;
        });
        
        // 添加适量的空间，确保版权信息可见，但不会过多
        // 减少空白区域的大小，使其更合理
        if (this.isMobile) {
          // 移动端使用较小的空间
          renderedHtml += '<div style="min-height: 10px;"></div>';
        } else {
          // PC端使用较小的空间
          renderedHtml += '<div style="min-height: 10px;"></div>';
        }
        
        this.renderedContent = renderedHtml;
        
        // 在下一个渲染周期处理LaTeX公式和代码高亮
        this.$nextTick(async () => {
          // 确保版权信息显示
          this.checkCopyrightInfo();
          
          // 先扫描标题生成导航
          this.scanHeadingsAndGenerateNavigation();
          
          // 手动处理引用样式
          this.enhanceBlockquotes();
          
          // 处理任务列表样式
          this.enhanceTaskLists();
          
          // 处理LaTeX公式
          if (window.MathJax && window.MathJax.Hub) {
            console.log('使用已加载的MathJax处理公式');
            window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, this.$refs.markdownDisplay]);
            
            // 处理完公式后处理颜色
            window.MathJax.Hub.Queue(() => {
              this.enhanceMathColors();
              
              // 触发渲染完成事件
              const event = new CustomEvent('mathjax-rendered');
              document.dispatchEvent(event);
              
              // 再次检查版权信息
              this.checkCopyrightInfo();
              
              // 修复移动端滚动问题
              if (this.isMobile) {
                this.fixMobileScroll();
              }
            });
          } else {
            // 如果MathJax还没有加载完成，等待加载完成后再渲染
            console.log('等待MathJax加载后处理公式');
            
            // 创建一次性事件处理函数
            document.addEventListener('mathjax-rendered', () => {
              if (window.MathJax && window.MathJax.Hub) {
                window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, this.$refs.markdownDisplay]);
                
                // 处理完后再次检查版权信息
                window.MathJax.Hub.Queue(() => {
                  this.checkCopyrightInfo();
                  
                  // 修复移动端滚动问题
                  if (this.isMobile) {
                    this.fixMobileScroll();
                  }
                });
              }
            }, { once: true });
          }
          
          // 处理代码高亮
          await this.highlightCode();
          
          // 处理完成后再次检查版权信息并修复滚动问题
          this.checkCopyrightInfo();
          if (this.isMobile) {
            this.fixMobileScroll();
          }
        });
        
      } catch (e) {
        console.error('渲染Markdown内容失败:', e);
      }
    },

    // 预处理自定义容器
    preprocessCustomContainers(content) {
      const icon = { info:'ℹ️', tip:'💡', warning:'⚠️', danger:'❌', details:'▶️' }
      const color = { info:'#409EFF', tip:'#67C23A', warning:'#E6A23C', danger:'#F56C6C', details:'#909399' }

      // Step 1: 把所有 fenced code block 预渲染为 HTML，然后用占位符替换
      // 原因：最终 container div 内的代码块必须已经是 HTML，否则 marked.parse() 会把它当作原始文本
      const codeBlocks = []
      let cbIdx = 0
      content = content.replace(/```[\s\S]*?```/g, (match) => {
        // 预渲染为 HTML，这样放在 container div 内时 marked.parse() 不会二次处理
        const html = `<pre><code class="language-${(match.match(/```(\w+)/) || ['',''])[1] || 'plaintext'}">${match.replace(/```\w*\n?/g, '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`
        const placeholder = `\u200BCB${cbIdx}\u200B`
        codeBlocks.push(html)
        cbIdx++
        return placeholder
      })

      let iterations = 0
      const maxIterations = 100

      while (iterations < maxIterations) {
        iterations++

        const opens = [], closes = []
        let m
        const allRe = /:::/g
        while ((m = allRe.exec(content)) !== null) {
          const pos = m.index
          const after = content[pos + 3]
          if (after !== undefined && /\w/.test(after)) {
            const typeMatch = content.substring(pos).match(/^:::(\w+)/)
            if (!typeMatch) continue
            const type = typeMatch[1]
            const titleMatch = content.substring(pos).match(/\[([^\]]*)\]/)
            const title = titleMatch ? titleMatch[1] : ''
            const lineEndMatch = content.substring(pos).search(/\n/)
            const lineEnd = lineEndMatch >= 0 ? pos + lineEndMatch : content.length
            opens.push({ start: pos, type, title, bodyStart: lineEnd + 1 })
          } else {
            const c1 = content[pos + 3]
            closes.push({ start: pos, lineEnd: c1 === '\n' ? pos + 3 : c1 === '\r' ? pos + 4 : content.length })
          }
        }

        let pair = null
        for (let oi = opens.length - 1; oi >= 0; oi--) {
          const open = opens[oi]
          let close = null
          for (const c of closes) { if (c.start > open.start) { close = c; break } }
          if (!close) continue
          const body = content.substring(open.bodyStart, close.start)
          if (!/^:::\w+/gm.test(body)) { pair = { open, close, body }; break }
        }
        if (!pair) break

        // 关键修复：用 marked 先把 body 预渲染为 HTML
        // 这样 bold/table/列表等都能正确渲染，而不是被 marked 当作原始文本
        // 内部已有的 container div（innermost 已处理）在这里会被 marked 当作 opaque，不受影响
        //
        // 修复：在 marked 解析前，先保护 body 中的 LaTeX 表达式，避免 $...$ 和 {} 等字符干扰列表解析
        // 问题：旧正则 \$[^\$\n]+?\$ 会把 \frac{...} 中的 $ 误认为内联公式结束符
        // 解决方案：用字符遍历+括号深度计数的方式匹配公式
        const bodyMathPlaceholders = []
        const protectMath = (text) => {
          let result = ''
          let i = 0
          while (i < text.length) {
            // 处理 $$...$$ 行间公式
            if (text[i] === '$' && text[i+1] === '$') {
              let j = i + 2
              let depth = 0
              while (j < text.length && !(text[j] === '$' && text[j+1] === '$' && depth === 0)) {
                if (text[j] === '{') depth++
                else if (text[j] === '}') depth--
                j++
              }
              const formula = text.substring(i, j + 2)
              bodyMathPlaceholders.push(formula)
              result += `MATH_PH_${bodyMathPlaceholders.length - 1}_END`
              i = j + 2
            }
            // 处理 $...$ 内联公式（但不在花括号内）
            else if (text[i] === '$' && text[i+1] !== '$') {
              let j = i + 1
              let depth = 0
              let inBrace = false
              while (j < text.length) {
                if (text[j] === '{' && !inBrace) {
                  inBrace = true
                  depth++
                } else if (text[j] === '}' && inBrace) {
                  depth--
                  if (depth === 0) inBrace = false
                } else if (text[j] === '$' && text[j-1] !== '\\' && depth === 0 && !inBrace) {
                  // 排除转义序列 \$，只有未被反斜杠转义的 $ 才是公式边界
                  break
                }
                j++
              }
              const formula = text.substring(i, j + 1)
              bodyMathPlaceholders.push(formula)
              result += `MATH_PH_${bodyMathPlaceholders.length - 1}_END`
              i = j + 1
            }
            // 普通字符
            else {
              result += text[i]
              i++
            }
          }
          return result
        }
        const protectedBody = protectMath(pair.body)
        const bodyHtml = marked.parse(protectedBody.trim()).replace(/MATH_PH_(\d+)_END/g, (_, idx) => bodyMathPlaceholders[parseInt(idx)])

        const t = pair.open.type.toLowerCase()
        const defaultCollapsed = true
        const collapsed = defaultCollapsed ? ' collapsed' : ''
        const ico = icon[t] || 'ℹ️'
        const col = color[t] || '#409EFF'
        const titleHtml = pair.open.title ? `<span class="container-title">${marked.parse(pair.open.title).replace(/<\/?p>/g, '')}</span>` : ''
        content = content.substring(0, pair.open.start) +
          `<div class="custom-container${collapsed}" data-type="${t}" style="border-color:${col};background:rgba(${this._hexToRgb(col)},0.08);margin:16px 0;border-radius:6px;border-width:1px;border-style:solid;">` +
          `<div class="container-header" style="padding:10px 14px;gap:8px;display:flex;align-items:center;cursor:pointer;"><span class="container-icon" style="font-size:16px;">${ico}</span>${titleHtml}<span class="container-toggle" style="font-size:12px;color:#909399;margin-left:auto;">▶</span></div>` +
          `<div class="container-content" style="padding:10px 14px 12px 20px;font-size:14px;line-height:1.7;color:#2c3e50;background:rgba(255,255,255,0.5);border-top:1px dashed rgba(0,0,0,0.1);">${bodyHtml}</div></div>` +
          content.substring(pair.close.lineEnd)
      }

      // Step 2: 还原代码块（已预渲染为 HTML），直接替换占位符
      // 不再需要 ZWSP 转义（代码块已经是 HTML，不会被二次处理）
      for (let i = 0; i < codeBlocks.length; i++) {
        content = content.split(`\u200BCB${i}\u200B`).join(codeBlocks[i])
      }

      return content
    },
    _hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result ? `${parseInt(result[1],16)},${parseInt(result[2],16)},${parseInt(result[3],16)}` : '64,158,255'
    },

    // 预处理LaTeX颜色命令
    preprocessLatexColors(content) {
      console.log('预处理LaTeX颜色命令');
      
      // 调试：输出原始内容片段
      if (content.includes('$$\\color') || content.includes('$$\\red')) {
        console.log('发现双美元符号颜色公式:', content.substring(0, 100));
      }
      
      // 处理LaTeX公式中的<>符号，在<>两边添加空格，避免被解析为HTML标签
      content = content.replace(/(\$.*?)([^\\])<([^\\])(.*?\$)/g, '$1$2 < $3$4'); // 单美元符号内的<
      content = content.replace(/(\$.*?)([^\\])>([^\\])(.*?\$)/g, '$1$2 > $3$4'); // 单美元符号内的>
      content = content.replace(/(\$\$.*?)([^\\])<([^\\])(.*?\$\$)/g, '$1$2 < $3$4'); // 双美元符号内的<
      content = content.replace(/(\$\$.*?)([^\\])>([^\\])(.*?\$\$)/g, '$1$2 > $3$4'); // 双美元符号内的>
      
      // 处理没有空格的情况，如$i>j$
      content = content.replace(/(\$[^\$]*?)<([^\$\s]*?\$)/g, '$1 < $2');
      content = content.replace(/(\$[^\$]*?)>([^\$\s]*?\$)/g, '$1 > $2');
      content = content.replace(/(\$\$[^\$]*?)<([^\$\s]*?\$\$)/g, '$1 < $2');
      content = content.replace(/(\$\$[^\$]*?)>([^\$\s]*?\$\$)/g, '$1 > $2');
      
      // 处理单美元符号的情况下的颜色命令
      content = content.replace(/\$\\red\{([^}]*)\}\$/g, '$\\color{red}{$1}$');
      content = content.replace(/\$\\blue\{([^}]*)\}\$/g, '$\\color{blue}{$1}$');
      content = content.replace(/\$\\green\{([^}]*)\}\$/g, '$\\color{green}{$1}$');
      content = content.replace(/\$\\purple\{([^}]*)\}\$/g, '$\\color{purple}{$1}$');
      content = content.replace(/\$\\orange\{([^}]*)\}\$/g, '$\\color{orange}{$1}$');
      
      // 处理不带花括号的命令: $\redXXX$ -> $\color{red}{XXX}$（只处理单美元符号）
      content = content.replace(/\$\\red(\S+)\$/g, '$\\color{red}{$1}$');
      content = content.replace(/\$\\blue(\S+)\$/g, '$\\color{blue}{$1}$');
      content = content.replace(/\$\\green(\S+)\$/g, '$\\color{green}{$1}$');
      content = content.replace(/\$\\purple(\S+)\$/g, '$\\color{purple}{$1}$');
      content = content.replace(/\$\\orange(\S+)\$/g, '$\\color{orange}{$1}$');
      
      return content;
    },
    
    // 增强引用样式
    enhanceBlockquotes() {
      if (!this.$refs.markdownDisplay) return;
      
      const markdownContent = this.$refs.markdownDisplay.querySelector('.markdown-content');
      if (!markdownContent) return;
      
      const blockquotes = markdownContent.querySelectorAll('blockquote');
      blockquotes.forEach(blockquote => {
        // 确保添加自定义类
        blockquote.classList.add('custom-blockquote');
        
        // 添加引号图标
        if (!blockquote.querySelector('.quote-icon')) {
          const quoteIcon = document.createElement('span');
          quoteIcon.className = 'quote-icon';
          quoteIcon.innerHTML = '❝';
          blockquote.insertBefore(quoteIcon, blockquote.firstChild);
        }
      });
    },
    
    // 增强任务列表样式
    enhanceTaskLists() {
      if (!this.$refs.markdownDisplay) return;
      
      // 拆分混合UL：同一UL内既有任务项又有普通项
      const uls = this.$refs.markdownDisplay.querySelectorAll('ul');
      uls.forEach(ul => {
        const lis = Array.from(ul.children).filter(n => n.tagName && n.tagName.toLowerCase() === 'li');
        if (!lis.length) return;
        const taskLis = lis.filter(li => li.querySelector('input[type="checkbox"]'));
        const normalLis = lis.filter(li => !li.querySelector('input[type="checkbox"]'));
        if (taskLis.length && normalLis.length) {
          const taskUl = document.createElement('ul');
          taskUl.className = 'task-list';
          taskUl.style.setProperty('padding-left', '20px', 'important');
          taskUl.style.setProperty('list-style', 'none', 'important');
          taskLis.forEach(li => taskUl.appendChild(li));
          const firstIsTask = lis[0].querySelector('input[type="checkbox"]') != null;
          if (firstIsTask) {
            ul.parentNode.insertBefore(taskUl, ul);
          } else {
            ul.parentNode.insertBefore(taskUl, ul.nextSibling);
          }
          ul.style.setProperty('padding-left', '40px', 'important');
        }
      });
      
      // 查找所有包含复选框的列表项
      const taskListItems = this.$refs.markdownDisplay.querySelectorAll('ul li:has(input[type="checkbox"])');
      
      taskListItems.forEach(item => {
        // 添加任务列表项类
        item.classList.add('task-list-item');
        
        // 确保复选框样式正确
        const checkbox = item.querySelector('input[type="checkbox"]');
        if (checkbox) {
          checkbox.style.margin = '0 8px 0 0';
          checkbox.style.transform = 'scale(1)';
          checkbox.style.flexShrink = '0';
          checkbox.style.verticalAlign = 'middle';
          checkbox.style.position = 'relative';
          checkbox.style.top = '-1px';
        }
        
        // 设置列表项为flex布局并允许换行，子级列表独占一行
        item.style.display = 'flex';
        item.style.flexWrap = 'wrap';
        item.style.alignItems = 'center';
        item.style.listStyle = 'none';
        // 直接子段落清零外边距
        const directP = item.querySelector(':scope > p');
        if (directP) {
          directP.style.setProperty('margin-top', '0', 'important');
          directP.style.setProperty('margin-bottom', '0', 'important');
        }
        const childLists = item.querySelectorAll(':scope > ul, :scope > ol');
        childLists.forEach(cl => {
          cl.style.display = 'block';
          cl.style.width = '100%';
          cl.style.margin = '4px 0 0 0';
        });
        
        // 隐藏默认的列表标记
        item.style.setProperty('--marker', 'none');
        
        // 确保父级ul有正确的缩进
        const parentUl = item.closest('ul');
        if (parentUl) {
          parentUl.style.paddingLeft = '20px';
          parentUl.style.listStyle = 'none';
        }
      });
      
      // 对于不支持:has()选择器的浏览器，使用备用方法
      const allListItems = this.$refs.markdownDisplay.querySelectorAll('ul li, ol li');
      allListItems.forEach(item => {
        const checkbox = item.querySelector('input[type="checkbox"]');
        if (checkbox) {
          // 这是一个任务列表项
          item.classList.add('task-list-item');
          item.style.display = 'flex';
          item.style.alignItems = 'center';
          item.style.listStyle = 'none';
          const directP2 = item.querySelector(':scope > p');
          if (directP2) {
            directP2.style.setProperty('margin-top', '0', 'important');
            directP2.style.setProperty('margin-bottom', '0', 'important');
          }
          
          // 确保复选框样式正确
          checkbox.style.margin = '0 8px 0 0';
          checkbox.style.transform = 'scale(1)';
          checkbox.style.flexShrink = '0';
          checkbox.style.verticalAlign = 'middle';
          checkbox.style.position = 'relative';
          checkbox.style.top = '-1px';
          
          // 确保父级ul有正确的缩进
          const parentUl = item.closest('ul');
          if (parentUl) {
            parentUl.style.paddingLeft = '20px';
            parentUl.style.listStyle = 'none';
          }
        }
      });
      
      // 折叠与列表相邻的段落外边距（普通无序列表也清零）
      const paragraphs = this.$refs.markdownDisplay.querySelectorAll('p');
      paragraphs.forEach(p => {
        const prev = p.previousElementSibling;
        const next = p.nextElementSibling;
        if (next && (next.tagName.toLowerCase() === 'ul' || next.tagName.toLowerCase() === 'ol')) {
          p.style.setProperty('margin-bottom', '0', 'important');
        }
        if (prev && (prev.tagName.toLowerCase() === 'ul' || prev.tagName.toLowerCase() === 'ol')) {
          p.style.setProperty('margin-top', '0', 'important');
        }
        
        // 检测并处理空段落，避免空行导致的1em间距
        const textContent = p.textContent.trim();
        const hasOnlyBr = p.querySelectorAll('br').length > 0 && p.children.length === p.querySelectorAll('br').length;
        const isEmpty = textContent === '' || hasOnlyBr;
        
        if (isEmpty) {
          p.style.setProperty('margin-top', '0', 'important');
          p.style.setProperty('margin-bottom', '0', 'important');
          p.style.setProperty('margin-block-start', '0', 'important');
          p.style.setProperty('margin-block-end', '0', 'important');
        }
      });
      
      // 强制处理：任何在列表之间的段落都应该清零margin
      const allParagraphs = this.$refs.markdownDisplay.querySelectorAll('p');
      allParagraphs.forEach(p => {
        let prev = p.previousElementSibling;
        let next = p.nextElementSibling;
        
        // 跳过非元素节点
        while (prev && prev.nodeType !== Node.ELEMENT_NODE) {
          prev = prev.previousElementSibling;
        }
        while (next && next.nodeType !== Node.ELEMENT_NODE) {
          next = next.nextElementSibling;
        }
        
        // 如果前后都是列表，强制清零margin
        if (prev && (prev.tagName.toLowerCase() === 'ul' || prev.tagName.toLowerCase() === 'ol') &&
            next && (next.tagName.toLowerCase() === 'ul' || next.tagName.toLowerCase() === 'ol')) {
          p.style.setProperty('margin-top', '0', 'important');
          p.style.setProperty('margin-bottom', '0', 'important');
          p.style.setProperty('margin-block-start', '0', 'important');
          p.style.setProperty('margin-block-end', '0', 'important');
        }
        
        // 如果前面是任务列表，后面是普通列表，特别处理
        if (prev && prev.classList.contains('task-list') &&
            next && next.tagName.toLowerCase() === 'ul' && !next.classList.contains('task-list')) {
          p.style.setProperty('margin-top', '0', 'important');
          p.style.setProperty('margin-bottom', '0', 'important');
          p.style.setProperty('margin-block-start', '0', 'important');
          p.style.setProperty('margin-block-end', '0', 'important');
        }
      });
      
      // 专门强制处理非任务列表中的p标签margin
      const nonTaskListParagraphs = this.$refs.markdownDisplay.querySelectorAll('ul:not(.task-list) li > p');
      nonTaskListParagraphs.forEach(p => {
        p.style.setProperty('margin', '0', 'important');
        p.style.setProperty('margin-top', '0', 'important');
        p.style.setProperty('margin-bottom', '0', 'important');
        p.style.setProperty('margin-block-start', '0', 'important');
        p.style.setProperty('margin-block-end', '0', 'important');
        p.style.setProperty('margin-inline-start', '0', 'important');
        p.style.setProperty('margin-inline-end', '0', 'important');
      });
    },
    
    // 加载highlight.js
    async loadHighlightJs() {
      return new Promise((resolve, reject) => {
        // 如果已经加载，直接返回
        if (window.hljs) {
          resolve(window.hljs)
          return
        }
        
        // 加载highlight.js CSS - 更换为更好看的主题
        const cssLink = document.createElement('link')
        cssLink.rel = 'stylesheet'
        cssLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/styles/github-dark.min.css'
        document.head.appendChild(cssLink)
        
        // 加载highlight.js JS
        const script = document.createElement('script')
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/highlight.min.js'
        script.async = true
        
        script.onload = () => {
          // 加载C++语言包
          const cppScript = document.createElement('script')
          cppScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/languages/cpp.min.js'
          cppScript.async = true
          
          cppScript.onload = () => {
            resolve(window.hljs)
          }
          
          cppScript.onerror = (err) => {
            console.error('加载C++语言包失败:', err)
            // 即使C++语言包加载失败，仍然可以使用基本的highlight.js
            resolve(window.hljs)
          }
          
          document.head.appendChild(cppScript)
        }
        
        script.onerror = (err) => {
          console.error('加载highlight.js失败:', err)
          reject(err)
        }
        
        document.head.appendChild(script)
      })
    },
    
    // 添加清理代码块的方法
    cleanupCodeBlocks() {
      if (!this.$refs.markdownDisplay) return;
      
      // 查找所有已增强的代码块
      const enhancedBlocks = this.$refs.markdownDisplay.querySelectorAll('pre.enhanced');
      enhancedBlocks.forEach(pre => {
        // 移除所有行号元素
        const lineNumbers = pre.querySelectorAll('.line-numbers');
        lineNumbers.forEach(el => el.remove());
        
        // 从代码容器中提取代码元素
        const codeContainers = pre.querySelectorAll('.code-container');
        codeContainers.forEach(container => {
          const codeEl = container.querySelector('code');
          if (codeEl) {
            // 将代码元素移出容器
            container.removeChild(codeEl);
            pre.appendChild(codeEl);
          }
          // 移除容器
          container.remove();
        });
        
        // 移除复制按钮
        const copyBtns = pre.querySelectorAll('.copy-code-btn');
        copyBtns.forEach(btn => btn.remove());
        
        // 移除增强标记，以便重新初始化
        pre.classList.remove('enhanced');
      });
    },
    
    // 初始化代码块
    initCodeBlocks() {
      if (!this.$refs.markdownDisplay || !window.hljs) return;
      
      // 处理所有未增强的代码块
      const codeBlocks = this.$refs.markdownDisplay.querySelectorAll('pre:not(.enhanced) code');
      
      codeBlocks.forEach((codeEl, index) => {
        const pre = codeEl.parentElement;
        
        // 标记为已增强
        pre.classList.add('enhanced');
        
        // 获取原始代码内容
        const originalCode = codeEl.textContent;
        
        // 使用highlight.js进行高亮
        try {
          // 检测语言
          const languageClass = Array.from(codeEl.classList).find(cls => cls.startsWith('language-'));
          const language = languageClass ? languageClass.replace('language-', '') : '';
          
          // 高亮代码
          if (language && window.hljs.getLanguage(language)) {
            const result = window.hljs.highlight(originalCode, { language });
            codeEl.innerHTML = result.value;
            
            // 对C++代码做特殊处理
            if (language === 'cpp' || language === 'c') {
              this.enhanceCppSyntax(codeEl);
            }
          } else {
            window.hljs.highlightElement(codeEl);
          }
        } catch (err) {
          console.error('代码高亮失败:', err);
          // 失败时保持原始内容，但确保HTML标签被转义
          codeEl.textContent = originalCode;
        }
        
        // 添加行号
        const lines = codeEl.innerHTML.split('\n');
        if (lines.length > 0) {
          const lineNumbersEl = document.createElement('div');
          lineNumbersEl.className = 'line-numbers';
          
          // 生成行号
          const lineNumbers = Array.from(
            { length: lines.length },
            (_, i) => String(i + 1).padStart(2)
          ).join('\n');
          
          lineNumbersEl.textContent = lineNumbers;
          pre.appendChild(lineNumbersEl);
        }
        
        // 添加代码容器，确保代码内容不被行号遮盖
        const codeContainer = document.createElement('div');
        codeContainer.className = 'code-container';
        
        // 移动代码到新容器
        pre.appendChild(codeContainer);
        codeContainer.appendChild(codeEl);
        
        // 设置代码左侧边距，让代码靠左但不被行号遮盖
        codeEl.style.marginLeft = '0';
        codeEl.style.paddingLeft = '0';
        
        // 仅在非移动端添加左侧边距
        if (window.innerWidth > 768) {
          codeContainer.style.marginLeft = '50px';
        } else {
          // 移动端不添加左侧边距，防止滑动间距
          codeContainer.style.marginLeft = '0';
        }
        
        // 添加复制按钮
        const copyBtn = document.createElement('button');
        copyBtn.className = 'copy-code-btn';
        copyBtn.textContent = '复制';
        
        copyBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          
          try {
            // 尝试使用现代API
            await navigator.clipboard.writeText(originalCode);
            copyBtn.textContent = '已复制';
            
            this.$message({
              message: '复制成功',
              type: 'success',
              duration: 2000
            });
            
            setTimeout(() => {
              copyBtn.textContent = '复制';
            }, 2000);
          } catch (err) {
            console.error('现代API复制失败，尝试备用方法:', err);
            
            // 备用方法：创建临时textarea元素
            try {
              const textarea = document.createElement('textarea');
              textarea.value = originalCode;
              textarea.style.position = 'fixed';
              textarea.style.opacity = '0';
              document.body.appendChild(textarea);
              textarea.select();
              
              const successful = document.execCommand('copy');
              document.body.removeChild(textarea);
              
              if (successful) {
                copyBtn.textContent = '已复制';
                this.$message({
                  message: '复制成功',
                  type: 'success',
                  duration: 2000
                });
                
                setTimeout(() => {
                  copyBtn.textContent = '复制';
                }, 2000);
              } else {
                throw new Error('execCommand复制失败');
              }
            } catch (fallbackErr) {
              console.error('备用复制方法也失败:', fallbackErr);
              
              this.$message({
                message: '复制失败，请手动选择并复制代码',
                type: 'error',
                duration: 2000
              });
            }
          }
        });
        
        pre.appendChild(copyBtn);
      });
    },
    
    // 增强C++语法高亮
    enhanceCppSyntax(codeElement) {
      try {
        // 查找所有预处理器指令并添加特殊样式
        const content = codeElement.innerHTML;
        
        // 增强预处理器指令
        let enhancedContent = content.replace(
          /(&lt;[a-zA-Z0-9_.\/]+&gt;)/g, 
          '<span class="cpp-include-path">$1</span>'
        );
        
        // 增强宏定义和常数
        enhancedContent = enhancedContent.replace(
          /\b(#include|#define|#pragma|#ifdef|#ifndef|#endif|#if|#else|#elif)\b/g,
          '<span class="cpp-preprocessor">$1</span>'
        );
        
        codeElement.innerHTML = enhancedContent;
        
        // 添加自定义样式
        const styleEl = document.getElementById('cpp-syntax-style') || document.createElement('style');
        if (!styleEl.id) {
          styleEl.id = 'cpp-syntax-style';
          styleEl.textContent = `
            .cpp-include-path {
              color: #a5d6ff !important;
            }
            .cpp-preprocessor {
              color: #ff7b72 !important;
              font-weight: bold !important;
            }
            .language-cpp .hljs-meta {
              color: #ff7b72 !important;
            }
            .language-cpp .hljs-keyword {
              color: #ff7b72 !important;
            }
            .language-cpp .hljs-type {
              color: #ffa657 !important;
            }
            .language-cpp .hljs-number {
              color: #d2a8ff !important;
            }
            .language-cpp .hljs-comment {
              color: #8b949e !important;
              font-style: italic !important;
            }
            .language-cpp .hljs-string {
              color: #a5d6ff !important;
            }
            .language-cpp .hljs-built_in {
              color: #79c0ff !important;
            }
            .language-cpp .hljs-title.function_ {
              color: #d2a8ff !important;
            }
            .language-cpp .hljs-operator {
              color: #ff7b72 !important;
            }
            .language-cpp .hljs-punctuation {
              color: #c9d1d9 !important;
            }
          `;
          document.head.appendChild(styleEl);
        }
      } catch (e) {
        console.error('增强C++语法失败:', e);
      }
    },
    
    // 处理内容区域点击事件
    handleContentClick(e) {
      // 如果点击的是链接，阻止默认行为，自行处理
      if (e.target.tagName === 'A' && e.target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault()
        const id = e.target.getAttribute('href').substring(1)
        this.scrollToHeading(id)
      }
    },
    
    // 获取文章类型对应的样式
    getArticleTypeStyle(type) {
      const styleMap = {
        'OI': '#409EFF',
        '题解': '#67C23A',
        '游记': '#E6A23C',
        '生活': '#F56C6C',
        '学习': '#8E44AD',
        '科技': '#16A085',
        '情感': '#D35400',
        '游戏': '#2980B9',
        '随笔': '#27AE60',
        '其他': '#C0392B'
      }
      return styleMap[type] || ''
    },
    
    // 返回按钮点击事件
    goBack() {
      // 直接跳转到博客首页
      this.$router.push('/blog')
    },
    
    // 初始化滚动监听
    initScrollObserver() {
      // 使用Intersection Observer API监听标题元素
      if ('IntersectionObserver' in window) {
        this.scrollObserver = new IntersectionObserver(
          (entries) => {
            const visibleHeadings = entries
              .filter(entry => entry.isIntersecting)
              .map(entry => entry.target)
              .sort((a, b) => a.offsetTop - b.offsetTop)
            
            if (visibleHeadings.length > 0) {
              // 更新当前可见标题
              this.currentHeadingId = visibleHeadings[0].id
            }
          },
          {
            rootMargin: '-100px 0px -80% 0px', // 标题进入视窗顶部100px后触发
            threshold: 0
          }
        )
      }
    },
    
    // 从本地存储加载背景设置
    loadBgSettings() {
      try {
        // 尝试获取全局设置
        const settings = JSON.parse(localStorage.getItem('globalBgSettings'))
        if (settings) {
          // 确保背景图索引正确加载
          this.selectedBgIndex = settings.bgIndex !== undefined ? settings.bgIndex : -1
          
          // 透明度设置，确保正确处理0值
          this.contentTransparency = settings.contentTransparency !== undefined ? 
                                     settings.contentTransparency : 0.2
          this.bgTransparency = settings.bgTransparency !== undefined ? 
                               settings.bgTransparency : 1
          
          // 应用加载的设置
          this.applyBgSettings()
        }
      } catch (e) {
        console.error('加载背景设置失败:', e)
      }
    },
    
    // 处理storage事件，当localStorage变化时更新设置
    handleStorageChange(event) {
      if (event.key === 'globalBgSettings') {
        try {
          const settings = JSON.parse(event.newValue);
          if (settings) {
            // 使用相同的逻辑处理值
            this.appliedBgIndex = settings.bgIndex !== undefined ? settings.bgIndex : -1;
            this.appliedContentTransparency = settings.contentTransparency !== undefined ? 
                                             settings.contentTransparency : 0.2;
            this.appliedBgTransparency = settings.bgTransparency !== undefined ? 
                                        settings.bgTransparency : 1;
             
            // 同步更新当前编辑中的值
            this.selectedBgIndex = this.appliedBgIndex;
            this.contentTransparency = this.appliedContentTransparency;
            this.bgTransparency = this.appliedBgTransparency;
          }
        } catch (e) {
          console.error('同步背景设置失败:', e);
        }
      }
    },
    
    // 保存背景设置到本地存储
    saveBgSettings() {
      try {
        const settings = {
          bgIndex: this.appliedBgIndex,
          contentTransparency: this.appliedContentTransparency,
          bgTransparency: this.appliedBgTransparency
        }
        // 保存为全局设置
        localStorage.setItem('globalBgSettings', JSON.stringify(settings))
      } catch (e) {
        console.error('保存背景设置失败:', e)
      }
    },
    
    // 选择背景图片
    selectBackground(index) {
      this.selectedBgIndex = index
    },
    
    // 应用背景设置
    applyBgSettings() {
      this.appliedBgIndex = this.selectedBgIndex
      this.appliedContentTransparency = this.contentTransparency
      this.appliedBgTransparency = this.bgTransparency
      
      // 保存设置
      this.saveBgSettings()
    },
    
    // 切换目录显示
    toggleNavigation() {
      this.showNavigation = !this.showNavigation
      
      // 如果是第一次显示导航，确保已经生成了导航
      if (this.showNavigation && this.navigationItems.length === 0) {
        this.scanHeadingsAndGenerateNavigation()
      }
    },
    
    // 扫描标题并生成导航
    scanHeadingsAndGenerateNavigation() {
      if (!this.$refs.markdownDisplay) return
      
      // 清空导航项
      this.navigationItems = []
      
      // 查找内容区域中的所有标题元素
      const markdownContent = this.$refs.markdownDisplay.querySelector('.markdown-content')
      if (!markdownContent) return
      
      const headings = markdownContent.querySelectorAll('h1, h2, h3, h4, h5, h6')
      
      // 处理每个标题
      headings.forEach((heading, index) => {
        // 获取标题级别和文本
        const level = parseInt(heading.tagName.substring(1))
        const text = heading.textContent.trim()
        
        // 为标题添加唯一ID
        const id = heading.id || `heading-${index}`
        heading.id = id
        
        // 添加到导航项
        this.navigationItems.push({
          id: id,
          level: level,
          text: text
        })
        
        // 添加到滚动监听
        if (this.scrollObserver) {
          this.scrollObserver.observe(heading)
        }
      })
      
      // 如果有标题，则默认选中第一个
      if (this.navigationItems.length > 0) {
        this.currentHeadingId = this.navigationItems[0].id
      }
    },
    
    // 滚动到指定标题
    scrollToHeading(id) {
      const heading = document.getElementById(id)
      
      if (heading) {
        // 更新当前标题ID
        this.currentHeadingId = id
        
        // 平滑滚动到标题位置
        heading.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        })
      }
    },
    
    // 获取文章详情
    async getDetail(articleCode) {
      // 重置状态，防止残留
      this.isLocked = false
      try {
        const response = await getBlogArticle(articleCode)

        if (response.code === 200) {
          this.article = response.data
          this.readCount = response.data.read_count || 0

          // 加密文章：显示锁定状态，等用户输入密码
          if (this.article.has_password) {
            this.isLocked = true
            this.currentEncryptedCode = articleCode
            this.pwdErrorCount = 0
            this.pwdInput = ''
            this.pwdErrorMsg = ''
            return
          }

          // 增加阅读量
          this.increaseReadCount(articleCode)

          // 正常渲染markdown内容
          this.renderMarkdown(this.article.article_info)

          // 绑定自定义容器点击事件
          this.$nextTick(() => {
            const container = this.$refs.markdownDisplay
            console.log('[BlogArticle] $nextTick - container:', container)
            console.log('[BlogArticle] markdownDisplay ref:', this.$refs.markdownDisplay)
            if (container) {
              const headers = container.querySelectorAll('.container-header')
              console.log('[BlogArticle] found headers:', headers.length)
              headers.forEach(header => {
                const toggle = header.querySelector('.container-toggle')
                const box = header.closest('.custom-container')
                const content = box?.querySelector('.container-content')
                // 初始化 toggle 旋转
                if (toggle && box) {
                  const isCollapsed = box.classList.contains('collapsed')
                  toggle.style.transform = isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)'
                }
                // 初始化 content display（collapsed 状态）
                if (content && box?.classList.contains('collapsed')) {
                  content.style.display = 'none'
                }
                header.onclick = () => {
                  if (!box) return
                  const isCollapsed = box.classList.contains('collapsed')
                  if (isCollapsed) {
                    content.style.display = ''
                    toggle && (toggle.style.transform = 'rotate(90deg)')
                  } else {
                    content.style.display = 'none'
                    toggle && (toggle.style.transform = 'rotate(0deg)')
                  }
                  box.classList.toggle('collapsed')
                }
              })
            }
          })
        } else {
          this.$message.error(response.message || '获取文章详情失败')
        }
      } catch (error) {
        console.error('获取文章详情失败：', error)
        this.$message.error(error.message || '获取文章详情失败')
      }
    },
    
    // 密码弹窗（el-dialog 方式，无遮罩）
    handlePwdSubmit() {
      if (!this.pwdInput) {
        this.pwdErrorMsg = '请输入密码'
        return
      }
      this.pwdErrorMsg = ''
      this.verifyPassword(this.currentEncryptedCode, this.pwdInput)
    },

    handlePwdCancel() {
      this.pwdInput = ''
      this.pwdErrorMsg = ''
      this.$router.push('/blog')
    },

    async verifyPassword(articleCode, password) {
      try {
        const response = await verifyArticlePassword(articleCode, password)
        if (response.data.verified) {
          // 用密码重新请求文章详情，获取正文内容
          const articleResponse = await getBlogArticle(articleCode, password)
          if (articleResponse.code === 200) {
            this.article = articleResponse.data
            this.isLocked = false
            this.pwdInput = ''
            this.pwdErrorMsg = ''
            this.increaseReadCount(articleCode)
            this.renderMarkdown(this.article.article_info)
            this.$nextTick(() => {
              const container = this.$refs.markdownDisplay
              if (container) {
                const headers = container.querySelectorAll('.container-header')
                headers.forEach(header => {
                  const toggle = header.querySelector('.container-toggle')
                  const box = header.closest('.custom-container')
                  const content = box?.querySelector('.container-content')
                  // 初始化 toggle 旋转
                  if (toggle && box) {
                    const isCollapsed = box.classList.contains('collapsed')
                    toggle.style.transform = isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)'
                  }
                  // 初始化 content display（collapsed 状态）
                  if (content && box?.classList.contains('collapsed')) {
                    content.style.display = 'none'
                  }
                  header.onclick = () => {
                    if (!box) return
                    const isCollapsed = box.classList.contains('collapsed')
                    if (isCollapsed) {
                      content.style.display = ''
                      toggle && (toggle.style.transform = 'rotate(90deg)')
                    } else {
                      content.style.display = 'none'
                      toggle && (toggle.style.transform = 'rotate(0deg)')
                    }
                    box.classList.toggle('collapsed')
                  }
                })
              }
            })
          }
        } else {
          // 密码错误
          this.pwdErrorCount++
          const remaining = 3 - this.pwdErrorCount
          if (remaining > 0) {
            this.pwdErrorMsg = `密码错误，剩余 ${remaining} 次尝试机会`
            this.pwdInput = ''
          } else {
            this.$message.error('密码错误次数过多，已返回')
            this.pwdErrorCount = 0
            this.pwdInput = ''
            this.pwdErrorMsg = ''
            this.$router.push('/blog')
          }
        }
      } catch (error) {
        this.pwdErrorCount++
        const remaining = 3 - this.pwdErrorCount
        if (remaining > 0) {
          this.pwdErrorMsg = `密码错误，剩余 ${remaining} 次尝试机会`
          this.pwdInput = ''
        } else {
          this.$message.error('密码错误次数过多，已返回')
          this.pwdErrorCount = 0
          this.pwdInput = ''
          this.pwdErrorMsg = ''
          this.$router.push('/blog')
        }
      }
    },

    // 增加阅读量
    async increaseReadCount(articleCode) {
      try {
        await increaseArticleReadCount(articleCode)
        this.readCount++
      } catch (error) {
        console.error('增加阅读量失败：', error)
      }
    },

    // 处理背景设置更新事件
    handleBgSettingsUpdated(settings) {
      this.contentCardStyle = settings.contentCardStyle
      this.backgroundLayerStyle = settings.backgroundLayerStyle
      this.backgroundStyle = settings.backgroundStyle
      this.navigationCardStyle = settings.contentCardStyle
    },
    
    // 检测设备类型
    checkDeviceType() {
      this.isMobile = window.innerWidth <= 768;
      
      // 在设备类型变化时调整代码块样式
      this.adjustCodeBlocksOnResize();
      
      // 在窗口大小变化时调整目录显示状态
      if (this.isMobile) {
        this.showNavigation = false;
      }
      
      // 确保在设备类型变化时也正确处理全局移动端菜单按钮
      this.hideGlobalMobileMenu();
    },
    
    // 移动端目录点击处理（先关闭目录再跳转）
    scrollToHeadingMobile(id) {
      // 先关闭目录
      this.showNavigation = false
      
      // 然后跳转到目标位置
      this.$nextTick(() => {
        this.scrollToHeading(id)
      })
    },
    
    // 添加窗口大小变化时调整代码块样式的函数
    adjustCodeBlocksOnResize() {
      if (!this.$refs.markdownDisplay) return;
      
      const codeContainers = this.$refs.markdownDisplay.querySelectorAll('.code-container');
      codeContainers.forEach(container => {
        if (window.innerWidth <= 768) {
          // 移动端
          container.style.marginLeft = '0';
        } else {
          // PC端
          container.style.marginLeft = '50px';
        }
      });
    },
    
    // 修复移动端滚动问题
    fixMobileScroll() {
      if (!this.isMobile || !this.$refs.markdownDisplay) return;
      
      // 防止频繁调用，添加防抖
      if (this.scrollFixDebounceTimer) {
        clearTimeout(this.scrollFixDebounceTimer);
      }
      
      this.scrollFixDebounceTimer = setTimeout(() => {
        try {
          console.log('修复移动端滚动问题');
          
          // 获取滚动容器
          const scrollContainer = this.$refs.markdownDisplay;
          
          // 获取头部高度
          const headerHeight = document.querySelector('.article-header')?.offsetHeight || 0;
          
          // 计算可用高度 (视口高度减去头部高度)
          // 不再额外减去底部间距，让版权信息自然显示
          const availableHeight = window.innerHeight - headerHeight;
          
          // 设置滚动容器高度 - 使用固定值而不是百分比，确保初始加载和刷新后高度一致
          scrollContainer.style.height = `${availableHeight}px`;
          scrollContainer.style.maxHeight = `${availableHeight}px`;
          scrollContainer.style.overflowY = 'auto';
          scrollContainer.style.webkitOverflowScrolling = 'touch';
          scrollContainer.style.boxSizing = 'border-box';
          
          // 确保父容器不限制滚动
          const customMarkdownContainer = scrollContainer.parentElement;
          if (customMarkdownContainer) {
            customMarkdownContainer.style.height = `${availableHeight}px`;
            customMarkdownContainer.style.maxHeight = `${availableHeight}px`;
            customMarkdownContainer.style.overflow = 'hidden';
          }
          
          // 设置内容区域样式
          const contentContainer = document.querySelector('.content-container');
          if (contentContainer) {
            contentContainer.style.height = 'auto';
            contentContainer.style.overflow = 'visible';
          }
          
          // 设置文章内容区域样式
          const articleContent = document.querySelector('.article-content');
          if (articleContent) {
            articleContent.style.height = 'auto';
            articleContent.style.overflow = 'visible';
          }
          
          // 设置主内容包装器样式
          const mainContentWrapper = document.querySelector('.main-content-wrapper');
          if (mainContentWrapper) {
            mainContentWrapper.style.height = 'auto';
            mainContentWrapper.style.overflow = 'visible';
          }
          
          console.log(`设置滚动容器高度: ${availableHeight}px`);
        } catch (error) {
          console.error('修复移动端滚动问题失败:', error);
        }
      }, 100); // 添加100ms延迟防抖
    },
    
    // 添加处理resize的统一方法
    handleResize() {
      this.checkDeviceType();
      this.adjustCodeBlocksOnResize();
      
      // 只有在移动端才需要修复滚动问题
      if (this.isMobile) {
        this.fixMobileScroll();
      }
    },
    
    // 检查版权信息的显示状态并确保它可见
    checkCopyrightInfo() {
      if (!this.$refs.copyrightInfo) return;
      
      try {
        // 使用模板中的引用获取版权信息元素
        const copyrightInfo = this.$refs.copyrightInfo;
        
        if (copyrightInfo) {
          // 根据设备类型应用正确的显示模式
          const pcCopyright = copyrightInfo.querySelector('.pc-copyright');
          const mobileCopyright = copyrightInfo.querySelector('.mobile-copyright');
          
          if (this.isMobile) {
            // 移动端样式
            if (pcCopyright) pcCopyright.style.display = 'none';
            if (mobileCopyright) mobileCopyright.style.display = 'flex';
          } else {
            // PC端样式
            if (pcCopyright) pcCopyright.style.display = 'inline';
            if (mobileCopyright) mobileCopyright.style.display = 'none';
          }
        }
      } catch (error) {
        console.error('检查版权信息失败:', error);
      }
    },
    
    // 处理代码高亮
    async highlightCode() {
      try {
        // 加载 highlight.js
        await this.loadHighlightJs();
        
        // 初始化代码块
        this.initCodeBlocks();
        
        // 确保版权信息存在
        this.checkCopyrightInfo();
        
        return true;
      } catch (error) {
        console.error('代码高亮处理失败:', error);
        return false;
      }
    },
    handleVisibilityChange() {
      // 只在页面变为可见时检查版权信息
      if (document.visibilityState === 'visible') {
        // 使用 setTimeout 确保 DOM 已经完全更新
        setTimeout(() => {
          this.checkCopyrightInfo();
          if (this.isMobile) {
            this.fixMobileScroll();
          }
        }, 100);
      }
    },
    // 确保版权信息始终可见的守护机制
    setupCopyrightGuardian() {
      // 清除可能已存在的计时器
      if (this.copyrightGuardianTimer) {
        clearInterval(this.copyrightGuardianTimer);
      }
      
      // 设置周期性检查版权信息的计时器（每2秒检查一次）
      this.copyrightGuardianTimer = setInterval(() => {
        this.checkCopyrightInfo();
      }, 2000);
      
      console.log('版权信息守护机制已启动');
    }
  }
}
</script>

<style lang="scss" scoped>
.blog-article {
  height: 100vh; /* 使用视口高度，确保占满整个屏幕 */
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  background-color: #f0f2f5;
  overflow: auto; /* 改为auto允许整体滚动 */
  position: relative; /* 添加相对定位 */

  // 添加这个类来覆盖App.vue中的全局移动端样式
  :deep(.mobile-menu-btn) {
    display: none !important; // 强制隐藏App.vue中的移动端菜单按钮
  }

  /* 背景图层 */
  .background-layer {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    pointer-events: none; /* 确保不会干扰鼠标事件 */
    z-index: 0; /* 放在最底层 */
    width: 100vw;
    height: 100vh;
  }

  /* 文章头部 */
  .article-header {
    margin: 8px auto 8px auto; /* 修改为上下8px，左右auto实现居中 */
    flex-shrink: 0;
    position: relative;
    z-index: 1; /* 确保在背景层之上 */
    max-width: 1200px;
    width: 100%; /* 修改：使用100%宽度 */
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12); /* 增强阴影效果 */

    /* 修改卡片内部背景为透明 */
    :deep(.el-card__body) {
      padding: 20px 20px 12px; /* 减小底部内边距从20px到12px */
      background-color: transparent;
    }

    /* 头部按钮组 */
    .header-buttons {
      position: absolute;
      bottom: 10px;
      right: 10px;
      display: flex;
      gap: 10px;
      z-index: 2;
    }

    /* 图标按钮通用样式 */
    .icon-btn {
      background-color: #409eff;
      color: #ffffff;
      border-color: #409eff;
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
      transform: translateY(0);
      transition: all 0.3s ease;
      
      i {
        font-size: 18px;
        margin: 0;
        line-height: 1;
      }
      
      &:hover, &:focus {
        background-color: #66b1ff;
        color: #ffffff;
        border-color: #66b1ff;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.25);
        transform: translateY(-2px);
      }
      
      &:active {
        background-color: #3a8ee6;
        color: #ffffff;
        border-color: #3a8ee6;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
        transform: translateY(1px);
      }
    }

    .header-content {
      .title-row {
        display: flex;
        align-items: center;
        margin-bottom: 8px; /* 从16px减少到8px，减少第一行与第二行之间的间距 */
        flex-wrap: wrap;
        gap: 12px;
        
        @media (max-width: 768px) {
          flex-direction: column;
          align-items: flex-start;
        }
        
        .article-type {
          flex-shrink: 0;
          font-size: 14px;
          padding: 4px 12px;
          border-radius: 4px;
          min-width: 60px;
          text-align: center;
          display: inline-flex;
          justify-content: center;
          align-items: center;
          height: 28px;
          line-height: 1;
          margin-right: 6px; /* 从12px减少到6px，减小文章类型与文章名称之间的间距 */
          font-weight: 600; /* 增加字体粗细 */
          box-shadow: none; /* 移除阴影，取消蓝色外圈 */
          border: none; /* 移除边框 */
        }
      }
      
      .article-title {
        font-size: 34px; /* 从32px增加到34px */
        font-weight: 600;
        color: #1f2937;
        margin: 0;
        line-height: 1.4;
        text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5); /* 添加文字阴影提高可读性 */
      }

      .time-info {
        font-size: 13px;
        color: #606266; /* 加深颜色，从#909399改为#606266 */
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 8px;
        margin-bottom: 0; /* 添加底部边距为0 */
        flex-wrap: wrap;
        font-weight: 500; /* 增加字体粗细 */

        span {
          display: flex;
          align-items: center;
          gap: 4px;
          
          i {
            margin-right: 4px;
            font-size: 14px;
          }
        }
        
        .divider {
          color: #909399; /* 保持分隔符颜色 */
          margin: 0 4px;
        }
        
        .article-tags {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;

          .article-tag {
            font-size: 12px;
            padding: 0 8px;
            height: 24px;
            line-height: 22px;
            border-radius: 4px;
            background-color: #ecf5ff;
            color: #409eff;
            border: 1px solid #d9ecff;
            font-weight: 500; /* 增加字体粗细 */
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); /* 添加轻微阴影 */

            &:hover {
              background-color: #409eff;
              border-color: #409eff;
              color: #fff;
            }
          }
        }
      }
    }
  }

  /* 内容区域容器 */
  .content-container {
    flex: 1;
    min-height: 0;
    position: relative;
    z-index: 1; /* 确保在背景层之上 */
    display: flex; /* 改为flex布局 */
    padding: 0; /* 移除左右内边距 */
    justify-content: center; /* 居中显示 */
    
    /* 主内容包装器 */
    .main-content-wrapper {
      position: relative; /* 改为相对定位，不再是绝对定位 */
      margin: 0 auto; /* 移除上下边距，只保留水平居中 */
      display: flex;
      max-width: 1200px;
      width: 100%; /* 修改：使用100%宽度 */
      flex: 1; /* 占满剩余空间 */
      
      /* 文章内容区域 */
      .article-content {
        flex: 1;
        min-width: 0;
        margin-right: 8px;
        transition: margin-right 0.3s ease, background-color 0.3s ease;
        height: 100%; /* 确保高度占满 */
        display: flex; /* 添加flex布局 */
        flex-direction: column; /* 垂直方向排列 */
        
        &.no-navigation {
          margin-right: 0;
        }
        
        :deep(.el-card__body) {
          padding: 0;
          height: 100%;
          background-color: transparent;
          display: flex; /* 添加flex布局 */
          flex-direction: column; /* 垂直方向排列 */
          flex: 1; /* 占满剩余空间 */
        }
        
        /* 自定义Markdown容器 */
        .custom-markdown-container {
          height: 100%;
          overflow: hidden;
          position: relative;
          flex: 1; /* 占满剩余空间 */
          display: flex; /* 添加flex布局 */
          flex-direction: column; /* 垂直方向排列 */
          
          .markdown-display {
            height: 100%;
            overflow-y: auto;
            padding: 20px;
            box-sizing: border-box;
            font-size: 15px;
            line-height: 1.75;
            color: #2c3e50;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;

            /* 文章内容 */
            .markdown-content {
              display: block;
            }

            /* 版权信息 */
            .copyright-info {
              text-align: center;
              padding: 10px 0 0;
              color: #909399;
              font-size: 13px;
              border-top: 1px solid #ebeef5;
              margin-top: 10px;
              white-space: normal;
              word-break: break-word;
              width: 100%;
              box-sizing: border-box;
              line-height: 1.6;
              position: relative;
              z-index: 10;

              .pc-copyright {
                display: inline;
              }

              .mobile-copyright {
                display: none;
                flex-direction: column;
                gap: 0;

                div {
                  line-height: 1.2;
                }
              }
            }
          }
        }
      }
      
      /* 导航卡片 */
      .navigation-card {
        width: 250px;
        flex-shrink: 0;
        background-color: rgba(255, 255, 255, 0.95);
        border-radius: 4px;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        display: flex;
        flex-direction: column;
        overflow: hidden;
  
        .navigation-header {
          padding: 12px 15px;
          background-color: #fff;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #ebeef5;
          
          .title-wrapper {
              font-size: 16px;
            font-weight: 600;
              color: #303133;
          }
          
          i {
            font-size: 18px;
            color: #909399;
            cursor: pointer;
            
            &:hover {
              color: #409eff;
            }
          }
        }
        
        .navigation-content {
          flex: 1;
          overflow-y: auto;
          
          .navigation-item {
            padding: 10px 15px;
            cursor: pointer;
            font-size: 14px;
            color: #606266;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            
            &.active {
              background-color: #ecf5ff;
              color: #409eff;
              border-right: 3px solid #409eff;
            }
          
            &:hover {
              color: #409eff;
              background-color: #f5f7fa;
            }
          }
          
          .navigation-empty {
          padding: 20px;
          text-align: center;
          color: #909399;
        }
      }
    }
  }
}
}

/* 适配移动端样式 */
@media screen and (max-width: 768px) {
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    height: 100% !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    position: relative !important;
  }
  
  #app {
    width: 100% !important;
    height: auto !important;
    min-height: 100% !important;
    overflow: visible !important;
    margin: 0 !important;
    padding: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }
  
  .blog-article {
    display: flex !important;
    flex-direction: column !important;
    height: auto !important;
    min-height: 100% !important;
    width: 100% !important;
    overflow: visible !important;
    padding: 0 !important;
    margin: 0 !important;
    position: relative !important;
    
    .article-header {
      margin: 0 !important;
      width: 100% !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      background-color: rgba(255, 255, 255, 0.8) !important;
      position: static !important;
      flex-shrink: 0 !important;
      
      /* 隐藏头部按钮组 */
      .header-buttons {
        display: none !important;
      }
      
      :deep(.el-card__body) {
        padding: 0 !important;
        background-color: transparent !important;
        width: 100% !important;
        box-sizing: border-box !important;
      }
      
      /* 修复头部样式 */
      .header-content.mobile-only {
        padding: 12px 15px 15px !important;
        box-sizing: border-box !important;
        width: 100% !important;
        overflow: visible !important;
  
        .article-title {
          font-size: 20px !important;
          line-height: 1.3 !important;
          margin: 0 0 10px 0 !important;
          font-weight: 600 !important;
          color: #1f2937 !important;
          text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3) !important;
        }
  
        .time-item {
          font-size: 12px !important;
          color: #4b5563 !important;
          margin-bottom: 4px !important;
          display: flex !important;
          align-items: center !important;
          
          i {
            margin-right: 4px !important;
            font-size: 13px !important;
          }
        }
  
        .meta-row {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          flex-wrap: wrap !important;
          margin-top: 8px !important;
          width: 100% !important;
          box-sizing: border-box !important;
          padding: 0 !important;
    
          .left-items {
            display: flex !important;
            align-items: center !important;
            flex-wrap: wrap !important;
            gap: 6px !important;
            flex: 1 !important;
            max-width: 75% !important;
          }
    
          .article-type {
            font-size: 12px !important;
            padding: 1px 8px !important;
            height: 22px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
            box-shadow: none !important;
            border: none !important;
          }
    
          .article-tag {
            font-size: 11px !important;
            padding: 0 6px !important;
            height: 20px !important;
            line-height: 18px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
    
          .read-count {
            font-size: 12px !important;
            color: #4b5563 !important;
            display: flex !important;
            align-items: center !important;
            white-space: nowrap !important;
            padding-left: 10px !important;
            margin-right: 0 !important;
            flex-shrink: 0 !important;
            
            i {
              margin-right: 3px !important;
              font-size: 13px !important;
            }
          }
        }
      }
    }
    
    .content-container {
      padding: 0 !important;
      width: 100% !important;
      margin-top: 5px !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      margin-bottom: 0 !important;
      box-sizing: border-box !important;
      flex: 1 !important;
      display: flex !important;
      flex-direction: column !important;
      position: relative !important;
      overflow: visible !important;
      
      .main-content-wrapper {
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        flex: 1 !important;
        display: flex !important;
        flex-direction: column !important;
        position: relative !important;
        overflow: visible !important;
        
        .article-content {
          margin: 0 !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          width: 100% !important;
          background-color: rgba(255, 255, 255, 0.8) !important;
          flex: 1 !important;
          margin-bottom: 0 !important;
          position: relative !important;
          overflow: visible !important;
          
          :deep(.el-card__body) {
            padding: 0 !important;
            margin: 0 !important;
            width: 100% !important;
            box-sizing: border-box !important;
            display: flex !important;
            flex-direction: column !important;
            flex: 1 !important;
            position: relative !important;
            overflow: visible !important;
          }
          
          .custom-markdown-container {
            width: 100% !important;
            margin-bottom: 0 !important;
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
            position: relative !important;
            overflow: visible !important;
            
            .markdown-display {
              padding: 15px 15px 0 15px !important;
              width: 100% !important;
              box-sizing: border-box !important;
              flex: 1 !important;
              position: relative !important;
              overflow-y: auto !important;
              -webkit-overflow-scrolling: touch !important;
              min-height: 0 !important;
              height: calc(100vh - 180px) !important; /* 增加高度，确保有足够空间显示版权信息 */
              
              .copyright-info {
                margin-bottom: 0 !important;
                padding-bottom: 15px !important;
              }
            }
          }
        }
      }
    }
  }
  
  /* 修复卡片样式 */
  .el-card {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    border-radius: 0 !important;
    border-left: none !important;
    border-right: none !important;
    box-sizing: border-box !important;
    margin-bottom: 0 !important;
  }
  
  /* 移除底部空白区域 */
  .blog-article::after {
    display: none !important;
    content: none !important;
  }
  
  /* 确保移动端按钮组显示在底部 */
  .mobile-action-buttons {
    position: fixed !important;
    right: 15px !important;
    bottom: 20px !important;
    z-index: 2001 !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 15px !important;
    align-items: center !important;
    
    .mobile-button-row {
      width: 40px !important;
      height: 40px !important;
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
    }
    
    .mobile-icon-btn {
      width: 40px !important;
      height: 40px !important;
      border-radius: 40px !important;
      background-color: rgba(255, 255, 255, 0.5) !important;
      color: #409eff !important;
      border: none !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
      padding: 0 !important;
      transition: all 0.3s ease !important;
      margin: 0 !important;
      
      i {
        font-size: 20px !important;
      }
      
      &:hover, &:focus {
        background-color: rgba(255, 255, 255, 0.8) !important;
        transform: scale(1.05) !important;
      }
      
      &:active {
        background-color: rgba(255, 255, 255, 0.9) !important;
        transform: scale(0.95) !important;
      }
    }
  }
  
  /* 确保移动端背景设置按钮包装器正确显示 */
  .mobile-bg-settings-wrapper {
    display: flex !important;
    width: 40px !important;
    height: 40px !important;
    justify-content: center !important;
    align-items: center !important;
  }
  
  /* 暗色模式下移动端按钮样式 */
  @media (prefers-color-scheme: dark) {
    .mobile-action-buttons .mobile-icon-btn {
      background-color: rgba(30, 30, 30, 0.7) !important;
      color: #67b3fd !important;
      
      &:hover, &:focus {
        background-color: rgba(40, 40, 40, 0.9) !important;
      }
      
      &:active {
        background-color: rgba(50, 50, 50, 1) !important;
      }
    }
  }
  
  /* 移动端浮动目录样式 */
  .mobile-navigation-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    background-color: rgba(0, 0, 0, 0.5) !important;
    z-index: 3000 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  .mobile-navigation-card {
    width: 85% !important;
    max-width: 350px !important;
    max-height: 80% !important;
    border-radius: 8px !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    
    .mobile-navigation-header {
      padding: 12px 15px !important;
      display: flex !important;
      justify-content: space-between !important;
      align-items: center !important;
      border-bottom: 1px solid #ebeef5 !important;
      
      .title-wrapper {
        font-size: 16px !important;
        font-weight: 600 !important;
        color: #303133 !important;
      }
      
      i {
        font-size: 18px !important;
        color: #909399 !important;
        cursor: pointer !important;
        
        &:hover {
          color: #409eff !important;
        }
      }
    }
    
    .mobile-navigation-content {
      flex: 1 !important;
      overflow-y: auto !important;
      
      .mobile-navigation-item {
        padding: 10px 15px !important;
        font-size: 14px !important;
        color: #606266 !important;
        cursor: pointer !important;
        transition: all 0.3s !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        
        &.active {
          background-color: #ecf5ff !important;
          color: #409eff !important;
          border-right: 3px solid #409eff !important;
        }
        
        &:hover {
          background-color: #f5f7fa !important;
          color: #409eff !important;
        }
      }
      
      .mobile-navigation-empty {
        padding: 15px !important;
        text-align: center !important;
        color: #909399 !important;
        font-size: 14px !important;
      }

      /* 图片最大宽度不超出内容区域 */
      :deep(img) {
        max-width: 100% !important;
        height: auto !important;
  display: block !important;
    }
  }
}


    .el-input {
      width: 200px;
    }
  }

  .locked-error {
    color: #F56C6C;
    font-size: 12px;
    margin: 5px 0 0;
  }
</style>

<style>
/* 博客文章页面特定的全局样式，覆盖App.vue中的移动端样式 */
.blog-article-container .mobile-menu-btn {
  display: none !important; /* 强制隐藏App.vue中的移动端菜单按钮 */
}

/* 移动端背景设置按钮样式覆盖 */
.mobile-bg-settings-wrapper .bg-settings-btn {
  width: 40px !important;
  height: 40px !important;
  border-radius: 40px !important;
  background-color: rgba(255, 255, 255, 0.5) !important;
  color: #409eff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 0 !important;
  position: static !important;
  top: auto !important;
  right: auto !important;
}

.mobile-bg-settings-wrapper .bg-settings-btn:hover,
.mobile-bg-settings-wrapper .bg-settings-btn:focus {
  background-color: rgba(255, 255, 255, 0.8) !important;
  transform: scale(1.05) !important;
}

.mobile-bg-settings-wrapper .bg-settings-btn:active {
  background-color: rgba(255, 255, 255, 0.9) !important;
  transform: scale(0.95) !important;
}

/* 确保背景设置按钮内部的图标居中 */
.mobile-bg-settings-wrapper .bg-settings-btn i {
  margin: 0 !important;
  font-size: 20px !important;
}

/* 暗色模式下移动端背景设置按钮 */
@media (prefers-color-scheme: dark) {
  .mobile-bg-settings-wrapper .bg-settings-btn {
    background-color: rgba(30, 30, 30, 0.7) !important;
    color: #67b3fd !important;
  }
  
  .mobile-bg-settings-wrapper .bg-settings-btn:hover,
  .mobile-bg-settings-wrapper .bg-settings-btn:focus {
    background-color: rgba(40, 40, 40, 0.9) !important;
  }
  
  .mobile-bg-settings-wrapper .bg-settings-btn:active {
    background-color: rgba(50, 50, 50, 0.9) !important;
  }
}

/* 非移动端下确保宽度铺满 */
@media screen and (min-width: 769px) and (max-width: 1216px) {
  .article-header, 
  .content-container, 
  .main-content-wrapper {
    width: 100% !important;
    max-width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    border-radius: 0 !important;
  }
  
  .article-content {
    flex: 1 !important;
  }
  
  .navigation-card {
    margin-right: 0 !important;
  }
}

/* 解决iOS设备安全区域和宽度问题 */
@media screen and (max-width: 768px) {
  /* 处理iOS安全区域 */
  @supports (padding: max(0px)) {
    .blog-article,
    .content-container,
    .main-content-wrapper,
    .article-content,
    .article-header,
    .el-card {
      width: calc(100vw - env(safe-area-inset-left) - env(safe-area-inset-right)) !important;
      padding-left: max(0px, env(safe-area-inset-left)) !important;
      padding-right: max(0px, env(safe-area-inset-right)) !important;
    }
  }
  
  /* 隐藏可能的滚动条 */
  ::-webkit-scrollbar {
    display: none !important;
  }
  
  * {
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }
}

/* 移动端版权信息样式调整 */
@media screen and (max-width: 768px) {
  .copyright-info {
    padding: 5px 10px 0 !important; /* 减小顶部内边距，移除底部内边距 */
    margin-top: 5px !important;
    margin-bottom: 5px !important;
    font-size: 12px !important;
    line-height: 1.5 !important; /* 减小行高 */
    position: static !important;
    display: block !important;
    height: auto !important;
    overflow: visible !important;
    white-space: normal !important;
    
    .pc-copyright {
      display: none !important;
    }
    
    .mobile-copyright {
      display: flex !important;
      white-space: normal !important;
      
      div {
        line-height: 1.2 !important; /* 进一步减小行高 */
        margin-bottom: 0 !important; /* 确保没有额外的底部边距 */
      }

    }
  }
}

/* 版权信息样式 */
.copyright-info {
  text-align: center;
  padding: 8px 0 10px;
  color: #909399;
  font-size: 12px;
  margin-top: 5px;
  margin-bottom: 5px;
  border-top: 1px solid #ebeef5;
  width: 100%;
  display: block;
  visibility: visible;
  opacity: 1;
  position: relative;
  z-index: 100;
  
  .pc-copyright {
    display: inline;
  }
  
  .mobile-copyright {
    display: none;
  }
}

/* 移动端版权信息样式 */
@media screen and (max-width: 768px) {
  .copyright-info {
    padding: 6px 10px 8px;
    
    .pc-copyright {
      display: none;
    }
    
    .mobile-copyright {
display: flex;
      flex-direction: column;
      gap: 1px;
      width: 100%;
      text-align: center;
    }
  }
}

.locked-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;

  .locked-icon {
    font-size: 48px;
    color: #909399;
    margin-bottom: 15px;
  }

  .locked-title {
    font-size: 16px;
    color: #606266;
    margin: 0 0 5px;
  }

  .locked-desc {
    font-size: 13px;
    color: #909399;
    margin: 0 0 20px;
  }

  .locked-pwd-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    .el-input {
      width: 200px;
    }
  }

  .locked-error {
    color: #F56C6C;
    font-size: 12px;
    margin: 5px 0 0;
  }
}
</style>




